import { useEffect, useMemo, useRef, useState } from "react";
import { parseLocalDate } from "../../utils/commandCenter";
import { useStateValue } from "../../context/StateProvider";
import { formatDate, formatNumber } from "../../utils/helper";
import { commissionForAgent, dealCreditsAgent } from "../../utils/dealCredit";
import { rankRoster } from "../../utils/teamRoster";
import { useDealClientNames } from "../../hooks/useDealClientNames";
import YearFilterDropdown from "./YearFilterDropdown";
import StatDetails from "./../deals/StatDetails";
import StatDealsModal from "../reuseable/StatDealsModal";
import {
  Target,
  TrendingDown,
  CheckCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Calendar,
  BarChart3,
  Clock,
  Home,
  ArrowUp,
  ArrowDown,
  Activity,
  Filter,
  Users,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const MONTH_NAMES_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Median of a list of numbers; null if the list is empty.
function median(nums) {
  const xs = nums.filter((n) => Number.isFinite(n)).slice().sort((a, b) => a - b);
  if (xs.length === 0) return null;
  const mid = Math.floor(xs.length / 2);
  return xs.length % 2 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2;
}

// The GROSS commission a deal paid out, before it is split between the agent
// and the team lead. The yearly GCI goals on this page were set back when every
// commission column meant gross, so a percent-to-goal has to be measured in
// gross as well — dividing one person's personal slice by a gross-sized goal
// compares two different things and always reads low. Older deals never stored
// a gross at all, so fall back to the two slices added back together.
function grossCommission(deal) {
  const total = Number(deal?.totalCommissionamount) || 0;
  if (total) return total;
  return (
    (Number(deal?.agentCommissionamount) || 0) +
    (Number(deal?.teamleadCommissionamount) || 0)
  );
}

// Initials for the identity avatar.
function getInitials(user) {
  if (!user) return "?";
  const f = (user.firstname || "").trim();
  const l = (user.lastname || "").trim();
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  return (f || l || "?").slice(0, 2).toUpperCase();
}

// Deterministic avatar gradient based on user id, matching the mockup's variety.
const AVATAR_GRADIENTS = [
  "bg-[#1E293B]",
  "bg-gradient-to-br from-blue-900 to-blue-500",
  "bg-gradient-to-br from-amber-800 to-amber-500",
  "bg-gradient-to-br from-rose-700 to-rose-500",
  "bg-gradient-to-br from-green-800 to-green-500",
  "bg-gradient-to-br from-themeColor to-linkColor",
];
function getAvatarClass(user) {
  if (!user) return AVATAR_GRADIENTS[0];
  const seed = String(user.id || "").charCodeAt(0) || 0;
  return AVATAR_GRADIENTS[seed % AVATAR_GRADIENTS.length];
}

function TeamPerformanceDetail({
  id,
  childSelectedYear,
  isWidth = true,
  isShowMonthly = true,
  dashboardview,
  teamView = false,
  hideHeader = false, // hide the navy page header when embedding on dashboard
  hideUpcoming = false, // hide the in-component Upcoming Closings (shown elsewhere)
  topSlot = null, // optional content rendered right under the identity card
}) {
  const [{ user, users, deals, leadsource, listingAppointments }] =
    useStateValue();
  const [, setSelectedMonth] = useState(null);
  const [statspopup, setstatspopup] = useState(false);
  const [selectedstat, setselectedstat] = useState("");
  const [statsdata, setstatsdata] = useState([]);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [filterYear, setFilterYear] = useState(String(currentYear));
  const [yearDropdownVisible, setYearDropdownVisible] = useState(false);
  // When true, the "On Pace" calc includes pending deal volume — gives a more
  // optimistic projection. Default is closed-only (the conservative view).
  const [includePending, setIncludePending] = useState(false);
  const [agentDropdownVisible, setAgentDropdownVisible] = useState(false);
  const navigate = useNavigate();

  const today = new Date();
  const selectedUser = teamView
    ? null
    : users.find((u) => String(u.id) === String(id));

  // Active producers for team-wide aggregations and roster display.
  // Includes only Admins (Team Leads) and Employees (Agents).
  // Excludes deleted (type 1), disabled (type 2), accountants, and supervisors.
  const teamMembers = useMemo(
    () =>
      (users || []).filter(
        (u) =>
          u?.type !== 1 &&
          u?.type !== 2 &&
          (u?.role === "Admin" || u?.role === "Employee"),
      ),
    [users],
  );

  useEffect(() => {
    setSelectedMonth((prev) =>
      prev
        ? { month: prev.month, year: Number(filterYear) }
        : { month: currentMonth, year: Number(filterYear) },
    );
    childSelectedYear && childSelectedYear(selectedYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  // ── GOALS ──
  // Goals live on three legacy arrays per user:
  //   yearlyGoals[].amount             → GCI ($)
  //   closedvolumeyearlygoals[].amount → Volume ($)
  //   closedyearlygoals[].count        → Units (#)
  // Assemble a unified yearGoal object from those.
  const yearGoal = useMemo(() => {
    const goalsFromUser = (u) => {
      if (!u) return { amount: 0, volumeGoal: 0, unitsGoal: 0, hasAny: false };
      const gci = Number(
        u.yearlyGoals?.find((g) => g.year === selectedYear)?.amount || 0,
      );
      const volume = Number(
        u.closedvolumeyearlygoals?.find((g) => g.year === selectedYear)
          ?.amount || 0,
      );
      const units = Number(
        u.closedyearlygoals?.find((g) => g.year === selectedYear)?.count || 0,
      );
      return {
        amount: gci,
        volumeGoal: volume,
        unitsGoal: units,
        hasAny: gci > 0 || volume > 0 || units > 0,
      };
    };

    if (teamView) {
      const totals = teamMembers.reduce(
        (acc, u) => {
          const g = goalsFromUser(u);
          acc.amount += g.amount;
          acc.volumeGoal += g.volumeGoal;
          acc.unitsGoal += g.unitsGoal;
          if (g.hasAny) acc.hasAny = true;
          return acc;
        },
        {
          year: selectedYear,
          amount: 0,
          volumeGoal: 0,
          unitsGoal: 0,
          hasAny: false,
        },
      );
      return totals.hasAny ? totals : null;
    }
    const src = dashboardview ? user : selectedUser;
    const g = goalsFromUser(src);
    return g.hasAny ? { year: selectedYear, ...g } : null;
  }, [teamView, teamMembers, selectedUser, user, selectedYear, dashboardview]);

  // Single-credit filter shared by every deal computation below. A deal
  // belongs to exactly one person, so counts and volume can never double up.
  const matchesSelectedUser = (d) => {
    if (teamView) return true; // Team view: include every deal.
    if (!selectedUser) return false;
    return dealCreditsAgent(d, selectedUser.id);
  };

  // Commission for a single deal.
  // Team view → team's take-home; agent view → that person's own slice
  // (agent amount, team-lead amount, or both when they filled both roles).
  const commissionFor = (d) => {
    if (teamView) return Number(d.takehomecommissionamount || 0);
    return commissionForAgent(d, selectedUser?.id);
  };

  // ── UPCOMING TEAM CLOSINGS (Admin / Accountant) ──
  const upcomingTeamDeals = useMemo(() => {
    if (!deals || !selectedUser) return [];
    return deals
      .filter((d) => {
        const stage = d.dealStage?.toLowerCase();
        if (!d.closingdate) return false;
        if (stage !== "pending") return false;
        if (selectedUser?.role !== "Admin" && selectedUser?.role !== "Accountant")
          return false;
        // Everyone else's closings: the exact complement of this person's own
        // Upcoming Closings, so the two panels never overlap or leave a gap.
        return !dealCreditsAgent(d, selectedUser.id);
      })
      .sort((a, b) => new Date(a.closingdate) - new Date(b.closingdate));
  }, [deals, selectedUser]);

  const upcomingTeamCommission = upcomingTeamDeals.reduce(
    (s, d) => s + Number(d.takehomecommissionamount || 0),
    0,
  );

  // ── UPCOMING CLOSINGS (for this user, or team-wide if teamView) ──
  const upcomingDeals = useMemo(() => {
    if (!deals) return [];
    if (teamView) {
      return deals
        .filter((d) => {
          const stage = d.dealStage?.toLowerCase();
          return d.closingdate && stage === "pending";
        })
        .sort((a, b) => new Date(a.closingdate) - new Date(b.closingdate));
    }
    if (!selectedUser) return [];
    return deals
      .filter((d) => {
        const stage = d.dealStage?.toLowerCase();
        if (!d.closingdate) return false;
        if (stage !== "pending") return false;
        return dealCreditsAgent(d, selectedUser.id);
      })
      .sort((a, b) => new Date(a.closingdate) - new Date(b.closingdate));
  }, [deals, selectedUser, teamView]);

  const upcomingCommission = upcomingDeals.reduce(
    (s, d) =>
      s +
      (teamView
        ? Number(d.takehomecommissionamount || 0)
        : commissionForAgent(d, selectedUser?.id)),
    0,
  );

  // Master list of relevant deals: every deal this person is on, whether the
  // deal is credited to them or it simply pays them a slice. Role plays no
  // part — an Agent can be the team lead of record on a deal, and he still
  // has to be paid for it.
  const isOnDeal = (d) =>
    dealCreditsAgent(d, selectedUser?.id) ||
    commissionForAgent(d, selectedUser?.id) > 0;
  const matchedDeals = teamView
    ? deals.filter((d) => d.closingdate)
    : deals.filter(isOnDeal);
  // Stage-based widgets (Active Listings, Pipeline Distribution) must NOT
  // inherit that closingdate requirement: an open listing usually has no
  // closing date yet — the app deliberately never guesses one — so the All
  // Team view was dropping nearly every Active/Coming Soon/Pre-Market listing
  // and showing only the couple that happened to carry a date (2026-08-30).
  // Date math keeps using matchedDeals; stage lists use this.
  const stageDeals = teamView ? deals : deals.filter(isOnDeal);

  // Build monthly buckets for the selected year.
  const monthlyData = Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    const year = selectedYear;
    const monthlyDeals = matchedDeals.filter((d) => {
      const dd = parseLocalDate(d.closingdate);
      return dd.getFullYear() === year && dd.getMonth() + 1 === month;
    });

    const isPendingStage = (d) =>
      !["closed", "cancelled", "expired"].includes(d.dealStage?.toLowerCase());
    const closedDeals = monthlyDeals.filter(
      (d) =>
        d.dealStage?.toLowerCase() === "closed" && matchesSelectedUser(d),
    );
    const pendingDeals = monthlyDeals.filter(
      (d) => isPendingStage(d) && matchesSelectedUser(d),
    );

    // Counts and volume come off the credited lists above (one deal, one
    // owner). Dollars run over every deal this person was PAID on, credited
    // or not, so a referral fee still shows up for whoever earned it and
    // Closed + Pending always adds back up to Net.
    //
    // These two lists are the deals behind the GCI columns, and the GCI
    // columns are nothing but their sums — the same list, added up. That is
    // the whole repair: the GCI cell used to be built here on the paid basis
    // while the only list you could open behind it was the CREDITED list, so
    // Henry's $11,210.20 opened a drawer holding a single $7,666 deal and the
    // other $3,544.20 had no deal behind it anywhere on the screen.
    const closedPaidDeals = monthlyDeals.filter(
      (d) => d.dealStage?.toLowerCase() === "closed" && commissionFor(d) !== 0,
    );
    const pendingPaidDeals = monthlyDeals.filter(
      (d) => isPendingStage(d) && commissionFor(d) !== 0,
    );
    const closed = closedPaidDeals.reduce(
      (sum, d) => sum + commissionFor(d),
      0,
    );
    const pending = pendingPaidDeals.reduce(
      (sum, d) => sum + commissionFor(d),
      0,
    );

    const monthlyvolume = closedDeals.reduce(
      (sum, d) => sum + Number(d.offerPrice || 0),
      0,
    );
    const monthlypendingvolume = pendingDeals.reduce(
      (sum, d) => sum + Number(d.offerPrice || 0),
      0,
    );

    // The month's GROSS commission on the CREDITED deals — the numerator for
    // the "% of Goal" column, which divides by a goal that was set in gross.
    // closedCommission above is the dollars axis (this person's own slices,
    // including on deals credited to somebody else) and is a different, wider
    // list, so it cannot be the numerator over a gross-sized goal.
    const closedCreditedGross = closedDeals.reduce(
      (sum, d) => sum + grossCommission(d),
      0,
    );

    return {
      month,
      year,
      closedCommission: closed,
      pendingCommission: pending,
      netCommission: closed + pending,
      closedDealsCount: closedDeals.length,
      closedDeals,
      PendingDealsCount: pendingDeals.length,
      PendingDeals: pendingDeals,
      // The lists behind the two GCI cells, so each cell can open the deals
      // it is the sum of.
      closedPaidDeals,
      pendingPaidDeals,
      monthlyvolume,
      monthlypendingvolume,
      closedCreditedGross,
    };
  });

  // ── YTD AGGREGATES ──
  const PendingDealsCount = matchedDeals.filter(
    (d) =>
      !["closed", "cancelled", "expired"].includes(d.dealStage?.toLowerCase()) &&
      parseLocalDate(d.closingdate).getFullYear() === selectedYear &&
      matchesSelectedUser(d),
  );
  const closedDealsCount = matchedDeals.filter(
    (d) =>
      d.dealStage?.toLowerCase() === "closed" &&
      parseLocalDate(d.closingdate).getFullYear() === selectedYear &&
      matchesSelectedUser(d),
  );

  const ytdVolume = closedDealsCount.reduce(
    (sum, d) => sum + Number(d.offerPrice || 0),
    0,
  );
  const ytdPendingVolume = PendingDealsCount.reduce(
    (sum, d) => sum + Number(d.offerPrice || 0),
    0,
  );
  const ytdnetVolume = ytdVolume + ytdPendingVolume;

  // DOLLARS lists. matchedDeals already scoped these to every deal this person
  // is on — credited to them or simply paying them a slice — so they include a
  // referral fee or a team-lead slice on a deal credited to someone else. Never
  // render .length from these: they are dollars lists, and counting them would
  // book other people's units as this person's deals. The unit counts live in
  // closedDealsCount / PendingDealsCount above.
  const pendingDealsYTD = matchedDeals.filter((d) => {
    if (!d.closingdate) return false;
    if (parseLocalDate(d.closingdate).getFullYear() !== selectedYear) return false;
    if (
      ["closed", "cancelled", "expired"].includes(d.dealStage?.toLowerCase())
    )
      return false;
    return true;
  });
  const closedDealsYTD = matchedDeals.filter(
    (d) =>
      d.closingdate &&
      parseLocalDate(d.closingdate).getFullYear() === selectedYear &&
      d.dealStage?.toLowerCase() === "closed",
  );

  // The deals that actually PAID this person, closed and pending. Every GCI
  // figure on this page is the sum of one of these two lists and nothing else,
  // and every drawer opened from a GCI figure shows that same list, so the
  // rows always add back up to the number they were opened from. A deal that
  // is credited to him but paid him nothing carries no dollars, so it belongs
  // on the unit counts and not in a dollars list.
  const closedPaidDealsYTD = closedDealsYTD.filter(
    (d) => commissionFor(d) !== 0,
  );
  const pendingPaidDealsYTD = pendingDealsYTD.filter(
    (d) => commissionFor(d) !== 0,
  );

  const closedCommissionTotal = closedPaidDealsYTD.reduce(
    (s, d) => s + commissionFor(d),
    0,
  );
  const pendingCommissionTotal = pendingPaidDealsYTD.reduce(
    (sum, d) => sum + commissionFor(d),
    0,
  );
  const netCommissionDealsYTD = [
    ...closedPaidDealsYTD,
    ...pendingPaidDealsYTD,
  ];
  const netCommissionTotal = netCommissionDealsYTD.reduce(
    (sum, d) => sum + commissionFor(d),
    0,
  );

  // The same closed year on the CREDITED basis: gross commission on the deals
  // credited to this person. closedCommissionTotal above is the DOLLARS axis
  // (every slice he was paid, including on deals credited to a teammate) and
  // is the right number for a "what did I earn" headline. It is the wrong
  // numerator over a gross-sized goal, which is what this exists for.
  const closedCreditedGrossTotal = closedDealsCount.reduce(
    (s, d) => s + grossCommission(d),
    0,
  );

  // ── PRIOR YEAR (for YoY deltas) ──
  const priorYear = selectedYear - 1;
  const priorYearClosedDeals = matchedDeals.filter(
    (d) =>
      d.dealStage?.toLowerCase() === "closed" &&
      parseLocalDate(d.closingdate).getFullYear() === priorYear &&
      matchesSelectedUser(d),
  );
  // The YoY badge beside the big YTD Volume number is volumeYoy, further down.
  // It used to divide this year SO FAR by the WHOLE of last year, which is the
  // same trap as mixing a paid numerator with a credited denominator: eight
  // months of one year over twelve of another. A flat year read as a collapse,
  // and the Closed Commission badge sitting an inch away was already measured
  // to the same date last year, so two badges worded "% YoY" on one strip
  // disagreed by construction. Every YoY figure on this page is now measured
  // Jan 1 → today against Jan 1 → today.

  // Same-period prior year (Jan 1 → today's month/day) for apples-to-apples YoY.
  const cutoffMonth = today.getMonth();
  const cutoffDay = today.getDate();
  const withinSamePeriod = (dt) =>
    dt.getMonth() < cutoffMonth ||
    (dt.getMonth() === cutoffMonth && dt.getDate() <= cutoffDay);
  const priorSamePeriodClosed = priorYearClosedDeals.filter((d) =>
    withinSamePeriod(parseLocalDate(d.closingdate)),
  );
  // Prior-year GCI is measured the same way as this year's: every deal the
  // person was paid on, credited or not. Comparing a credited-only prior year
  // against a paid-in-full current year would fake the YoY swing.
  const priorSamePeriodGci = matchedDeals
    .filter(
      (d) =>
        d.dealStage?.toLowerCase() === "closed" &&
        parseLocalDate(d.closingdate).getFullYear() === priorYear &&
        withinSamePeriod(parseLocalDate(d.closingdate)),
    )
    .reduce((s, d) => s + commissionFor(d), 0);
  const priorSamePeriodVolume = priorSamePeriodClosed.reduce(
    (s, d) => s + Number(d.offerPrice || 0),
    0,
  );
  const priorSamePeriodUnits = priorSamePeriodClosed.length;
  const priorSamePeriodAvg =
    priorSamePeriodUnits > 0 ? priorSamePeriodVolume / priorSamePeriodUnits : 0;

  // YTD this year for each metric.
  const ytdGci = closedCommissionTotal;
  const ytdUnits = closedDealsCount.length;
  const ytdAvg = ytdUnits > 0 ? ytdVolume / ytdUnits : 0;

  // Display values that swap based on the "Include pending" toggle. When
  // ON, the tiles that aren't explicitly labeled "Closed" / "Pending" /
  // "Net" (YTD Volume headline, the Closed Deals count, GCI / Volume /
  // Units / Avg Sale Price in the YoY row, and the pace badge) reflect
  // closed + pending. Tiles that are explicitly named ("Closed
  // Commission", "Pending Commission", "YTD Pending Volume", "Net YTD
  // Volume", "Net Commission") stay locked to their named meaning so
  // their labels never lie.
  const displayVolume = includePending ? ytdnetVolume : ytdVolume;
  const displayUnits = includePending
    ? ytdUnits + PendingDealsCount.length
    : ytdUnits;
  const displayGci = includePending
    ? closedCommissionTotal + pendingCommissionTotal
    : ytdGci;
  const displayAvg = displayUnits > 0 ? displayVolume / displayUnits : 0;

  // Same-period YoY % deltas (null when prior had no data). The "display"
  // versions use the toggle-aware value so when "Include pending" is on,
  // the headline tile shows "$3.65M ↑X% YoY" reflecting closed+pending vs
  // prior same-period closed. The "closed-only" versions are used on
  // tiles whose value never includes pending (e.g. the hero's Closed
  // Commission badge) so the badge never lies about its own number.
  const samePeriodYoy = (curr, prior) =>
    prior > 0 ? ((curr - prior) / prior) * 100 : null;
  const closedGciYoy = samePeriodYoy(ytdGci, priorSamePeriodGci);
  const gciYoy = samePeriodYoy(displayGci, priorSamePeriodGci);
  const volumeYoy = samePeriodYoy(displayVolume, priorSamePeriodVolume);
  const unitsYoy = samePeriodYoy(displayUnits, priorSamePeriodUnits);
  const avgYoy = samePeriodYoy(displayAvg, priorSamePeriodAvg);

  // Prior-year monthly closed commission (for chart YoY trend line). Dollars,
  // so it runs over every deal the person was paid on, matching the current
  // year's bars it is drawn against.
  const priorYearMonthly = Array.from({ length: 12 }, (_, i) => {
    const monthDeals = matchedDeals.filter((d) => {
      const dd = parseLocalDate(d.closingdate);
      return (
        dd.getFullYear() === priorYear &&
        dd.getMonth() === i &&
        d.dealStage?.toLowerCase() === "closed"
      );
    });
    return monthDeals.reduce((s, d) => s + commissionFor(d), 0);
  });

  // ── GOAL PACING ──
  const gciGoal = yearGoal ? Number(yearGoal.amount || 0) : 0;
  const volumeGoal = yearGoal ? Number(yearGoal.volumeGoal || 0) : 0;
  const unitsGoal = yearGoal ? Number(yearGoal.unitsGoal || 0) : 0;
  // A GCI goal is a GROSS number: it was set when every commission column on
  // this page meant the deal's total commission, not one person's slice of it.
  // So both percent-to-goal figures below divide gross commission on the deals
  // CREDITED to this person by that goal. Dividing his personal slice
  // (closedCommissionTotal) by a gross-sized goal mixes two different bases and
  // permanently under-reads his pace.
  const GOAL_BASIS_NOTE =
    "Measured the way the goal was set: the gross commission on the deals credited to this person, not their personal slice.";
  const TotalPct =
    gciGoal > 0 ? ((closedCreditedGrossTotal / gciGoal) * 100).toFixed(1) : 0;
  const closedGciPacePct =
    gciGoal > 0 ? (closedCreditedGrossTotal / gciGoal) * 100 : null;
  const unitsPacePct =
    unitsGoal > 0 ? (displayUnits / unitsGoal) * 100 : null;

  const monthsElapsed = currentMonth + today.getDate() / 30;
  const expectedAtNow = (monthsElapsed / 12) * (volumeGoal || 0);
  const volumePacePct =
    volumeGoal > 0 ? (displayVolume / volumeGoal) * 100 : 0;
  // The CLOSED share of the goal, which is what the green segment of the
  // pacing bar and the marker beneath it mean. The bar draws pending as a
  // separate amber block stacked on top of the green one, so the green block
  // has to stop at closed volume. It used to be drawn at volumePacePct, and
  // volumePacePct already includes pending whenever the "Include pending"
  // toggle is on — so with the toggle on the bar drew the pending volume
  // twice, and the "$425,000 · 110%" marker under it printed closed dollars
  // beside a percent that had counted pending as well.
  const closedPacePct =
    volumeGoal > 0 ? (ytdVolume / volumeGoal) * 100 : 0;
  const expectedPacePct =
    volumeGoal > 0 ? (expectedAtNow / volumeGoal) * 100 : 0;
  // Pace volume — closed only, or closed + pending when the toggle is on.
  const paceVolume = includePending
    ? ytdVolume + ytdPendingVolume
    : ytdVolume;
  const runRatePerMonth = monthsElapsed > 0 ? paceVolume / monthsElapsed : 0;
  const projectedYearEnd = runRatePerMonth * 12;
  const projectedPct =
    volumeGoal > 0 ? (projectedYearEnd / volumeGoal) * 100 : 0;
  const monthsRemaining = Math.max(0, 12 - monthsElapsed);
  const needPerMonth =
    monthsRemaining > 0
      ? Math.max(0, (volumeGoal - paceVolume) / monthsRemaining)
      : 0;
  const endOfYear = new Date(selectedYear, 11, 31);
  const daysRemaining = Math.max(
    0,
    Math.ceil((endOfYear - today) / (1000 * 60 * 60 * 24)),
  );
  const isOnPace = projectedPct >= 95;

  // ── PIPELINE DISTRIBUTION (real data — open deals only) ──
  // One brand-blue ramp, light → dark through the funnel (sequential color:
  // one hue, ordered by stage depth — anchored on the link blue #218FDA and
  // ending at the brand navy). On Hold is parked, not a funnel step, so it
  // stays neutral. Identity is carried by the row labels; color shows depth.
  const PIPELINE_STAGES = [
    { key: "cma", label: "CMAs", color: "#A5CBEE" },
    { key: "listing appt", label: "Listing Appts", color: "#7FB5E6" },
    { key: "pre-market", label: "Pre-Market", color: "#55A0DE" },
    { key: "coming soon", label: "Coming Soon", color: "#218FDA" },
    { key: "active", label: "Active", color: "#1C6EA8" },
    { key: "under contract", label: "Under Contract", color: "#1D5079" },
    { key: "pending", label: "Pending", color: "#1E293B" },
    { key: "on hold", label: "On Hold", color: "#B7BFCB" },
  ];
  const pipelineRows = PIPELINE_STAGES.map((stage) => {
    const dealsInStage = stageDeals.filter((d) => {
      if (d.dealStage?.toLowerCase() !== stage.key || !matchesSelectedUser(d))
        return false;
      // An open deal with no closing date yet is TODAY's pipeline — count it
      // when viewing the current year instead of nowhere at all.
      if (!d.closingdate) return selectedYear === new Date().getFullYear();
      return parseLocalDate(d.closingdate).getFullYear() === selectedYear;
    });
    const volume = dealsInStage.reduce(
      (s, d) => s + Number(d.offerPrice || 0),
      0,
    );
    return { ...stage, count: dealsInStage.length, volume };
  });
  const maxPipelineCount = Math.max(1, ...pipelineRows.map((r) => r.count));
  const openPipelineVolume = pipelineRows.reduce((s, r) => s + r.volume, 0);
  const openPipelineCount = pipelineRows.reduce((s, r) => s + r.count, 0);

  // Active / Coming Soon / Pre-Market listings for this user — actual deal
  // records, not just counts. Used by the Active Listings panel below.
  const activeListings = stageDeals.filter(
    (d) => d.dealStage?.toLowerCase() === "active" && matchesSelectedUser(d),
  );
  const comingSoonListings = stageDeals.filter(
    (d) =>
      d.dealStage?.toLowerCase() === "coming soon" && matchesSelectedUser(d),
  );
  const expectedOpenGci = pendingCommissionTotal;

  // ── CYCLE TIME ANALYSIS ──
  // Scope listingAppointments to the selected year + the person whose page this
  // is. Team view takes everybody; a personal page takes only that person's
  // appointments, whatever their role. Team Leads used to be handed the whole
  // team's appointments here, which then fed the Conversion Funnel: the CMA
  // count was the team's while every stage after it was only that Team Lead's
  // own credited deals, so his pull-through rates read as a fraction of the
  // truth. Numerator and denominator now cover the same person.
  const scopedAppts = useMemo(() => {
    const all = listingAppointments || [];
    return all.filter((a) => {
      const apptYr = a.appointmentDate
        ? new Date(a.appointmentDate).getFullYear()
        : Number(a.forYear);
      if (apptYr !== selectedYear) return false;
      if (teamView) return true;
      if (!selectedUser) return false;
      return a.agentId === selectedUser.id;
    });
  }, [listingAppointments, selectedYear, teamView, selectedUser]);

  // Lead → Appointment: days between record creation and the appointment date.
  // Only count appointments where both dates exist and createdAt precedes
  // appointmentDate (filters out after-the-fact data entry).
  const cycleLeadToAppt = useMemo(() => {
    const days = scopedAppts
      .map((a) => {
        const created = a.createdAt ? new Date(a.createdAt) : null;
        const appt = a.appointmentDate ? new Date(a.appointmentDate) : null;
        if (!created || !appt) return null;
        const diff = (appt - created) / 86400000;
        return diff >= 0 ? diff : null;
      })
      .filter((n) => n !== null);
    return { median: median(days), sample: days.length };
  }, [scopedAppts]);

  // Lead → Close (Full): for closed deals in scope/year, days from deal
  // createdat to closingdate.
  const cycleLeadToClose = useMemo(() => {
    const days = closedDealsCount
      .map((d) => {
        const created = d.createdat ? new Date(Number(d.createdat)) : null;
        const closed = d.closingdate ? parseLocalDate(d.closingdate) : null;
        if (!created || !closed || isNaN(created) || isNaN(closed)) return null;
        const diff = (closed - created) / 86400000;
        return diff >= 0 ? diff : null;
      })
      .filter((n) => n !== null);
    return { median: median(days), sample: days.length };
  }, [closedDealsCount]);

  // Helper: per-deal day-diff between two date-ish fields.
  const dayDiff = (start, end) => {
    if (!start || !end) return null;
    const s = new Date(typeof start === "number" ? start : start);
    const e = new Date(typeof end === "number" ? end : end);
    if (isNaN(s) || isNaN(e)) return null;
    const diff = (e - s) / 86400000;
    return diff >= 0 ? diff : null;
  };

  // Listing → Under Contract: listedDate → underContractDate. Requires both
  // stamps (only populated for deals that have flipped through those stages
  // since the timestamp tracking was added).
  const cycleListToUc = useMemo(() => {
    const sellerInYear = matchedDeals.filter((d) => {
      if (d.dealtype !== "Seller") return false;
      if (!matchesSelectedUser(d)) return false;
      const yr = d.closingdate
        ? parseLocalDate(d.closingdate).getFullYear()
        : null;
      return yr === selectedYear;
    });
    const days = sellerInYear
      .map((d) => dayDiff(d.listedDate, d.underContractDate))
      .filter((n) => n !== null);
    return { median: median(days), sample: days.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedDeals, selectedYear, selectedUser, teamView]);

  // Contract → Close: underContractDate → closedDate.
  const cycleUcToClose = useMemo(() => {
    const days = closedDealsCount
      .map((d) => dayDiff(d.underContractDate, d.closedDate || d.closingdate))
      .filter((n) => n !== null);
    return { median: median(days), sample: days.length };
  }, [closedDealsCount]);

  // ── CONVERSION FUNNEL (seller-side) ──
  const funnel = useMemo(() => {
    const cmaCount = scopedAppts.length;
    const listedCount = scopedAppts.filter(
      (a) => a.status === "won" || a.status === "closed",
    ).length;

    // UC = seller-side deals at Under Contract / Pending / Closed in the year.
    const sellerDealsInYear = matchedDeals.filter((d) => {
      if (d.dealtype !== "Seller") return false;
      if (!matchesSelectedUser(d)) return false;
      const yr = d.closingdate
        ? parseLocalDate(d.closingdate).getFullYear()
        : null;
      return yr === selectedYear;
    });
    const ucCount = sellerDealsInYear.filter((d) =>
      ["under contract", "pending", "closed"].includes(
        (d.dealStage || "").toLowerCase(),
      ),
    ).length;
    const closedSellerCount = sellerDealsInYear.filter(
      (d) => (d.dealStage || "").toLowerCase() === "closed",
    ).length;

    const pct = (num, den) =>
      den > 0 ? `${Math.round((num / den) * 100)}%` : "—";

    return {
      cma: cmaCount,
      listed: listedCount,
      uc: ucCount,
      closed: closedSellerCount,
      cmaToList: pct(listedCount, cmaCount),
      listToUc: pct(ucCount, listedCount),
      ucToClosed: pct(closedSellerCount, ucCount),
      overall: pct(closedSellerCount, cmaCount),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedAppts, matchedDeals, selectedYear, selectedUser, teamView]);

  // ── LEAD SOURCES ──
  const leadSourceData = useMemo(() => {
    if (!leadsource || !closedDealsCount) return [];
    return leadsource
      .map((source) => {
        const matched = closedDealsCount.filter(
          (deal) => deal?.leadsource?.abbreviation === source?.abbreviation,
        );
        return {
          leadsourceId: source?.id,
          leadsourceAbbreviation: source?.abbreviation,
          leadsourceName: source?.name || source?.abbreviation,
          dealClosedCount: matched.length,
          deals: matched,
        };
      })
      .filter((s) => s.dealClosedCount > 0)
      .sort((a, b) => b.dealClosedCount - a.dealClosedCount);
  }, [leadsource, closedDealsCount]);
  const totalLeadSourceCount = leadSourceData.reduce(
    (s, r) => s + r.dealClosedCount,
    0,
  );
  const maxLeadSourceCount = Math.max(
    1,
    ...leadSourceData.map((r) => r.dealClosedCount),
  );
  const LEAD_SOURCE_COLORS = [
    "bg-[#1E293B]",
    "bg-themeColor",
    "bg-emerald-600",
    "bg-amber-700",
    "bg-slate-500",
  ];

  // ── PER-AGENT STATS (for Team Roster panel — only computed in teamView) ──
  const agentStats = useMemo(() => {
    if (!teamView) return [];
    const perAgentCommission = (d, agent) => commissionForAgent(d, agent.id);

    const isClosed = (d) => d.dealStage?.toLowerCase() === "closed";
    const isLive = (d) =>
      !["closed", "cancelled", "expired"].includes(d.dealStage?.toLowerCase());
    const inYear = deals.filter(
      (d) =>
        d.closingdate &&
        parseLocalDate(d.closingdate).getFullYear() === selectedYear,
    );

    return teamMembers
      .map((agent) => {
        // Units and volume: the one agent who owns the deal, so the roster
        // never counts a single transaction on two people's rows.
        const credited = inYear.filter((d) => dealCreditsAgent(d, agent.id));
        const closed = credited.filter(isClosed);
        const pending = credited.filter(isLive);

        // Dollars: every deal that actually paid this person, credited to him
        // or not, so a referral fee still lands on whoever earned it. Same
        // split as rosterStats in utils/teamRoster.js, which feeds this panel.
        const paid = inYear.filter(
          (d) => perAgentCommission(d, agent) !== 0,
        );
        const sumPay = (list) =>
          list.reduce((s, d) => s + perAgentCommission(d, agent), 0);

        return {
          user: agent,
          volume: closed.reduce((s, d) => s + Number(d.offerPrice || 0), 0),
          dealsClosed: closed.length,
          dealsActive: pending.length,
          gci: sumPay(paid.filter(isClosed)),
          pendingVolume: pending.reduce(
            (s, d) => s + Number(d.offerPrice || 0),
            0,
          ),
          pendingComm: sumPay(paid.filter(isLive)),
          // Kept so clicking the Deals number opens the actual list.
          closedDeals: closed,
          pendingDeals: pending,
        };
      })
      .sort((a, b) => b.volume - a.volume);
  }, [teamView, teamMembers, deals, selectedYear]);

  // ── ROSTER RANKING ──
  // Closed is what's banked, Pending is what's about to be, Total is both.
  // Ranking math is shared with the accountant dashboard's roster so the two
  // boards can never disagree about who's ahead.
  const [rosterMode, setRosterMode] = useState("closed");
  const [rosterMetric, setRosterMetric] = useState("volume");
  // Row whose Deals number was clicked — opens the drill-down list.
  const [rosterDealsFor, setRosterDealsFor] = useState(null);
  const rankedRoster = useMemo(
    () => rankRoster(agentStats, { mode: rosterMode, metric: rosterMetric }),
    [agentStats, rosterMode, rosterMetric],
  );
  const rosterTotals = useMemo(() => {
    const t = { closed: 0, pending: 0, deals: 0, active: 0 };
    agentStats.forEach((r) => {
      t.closed += rosterMetric === "gci" ? r.gci : r.volume;
      t.pending += rosterMetric === "gci" ? r.pendingComm : r.pendingVolume;
      t.deals += r.dealsClosed;
      t.active += r.dealsActive;
    });
    return t;
  }, [agentStats, rosterMetric]);

  // ── HERO DERIVED ──
  // An average per deal has to divide the commission on a set of deals by the
  // size of that SAME set. closedCommissionTotal above is the DOLLARS axis and
  // is wider than the credited list whenever a teammate's deal pays this person
  // a slice, so it cannot be the numerator over a credited count: the pair
  // invents money per deal that no deal ever paid. Both halves below are the
  // deals credited to him, and the line says so, because it is deliberately not
  // the headline's own set.
  const creditedClosedDealCount = closedDealsCount.length;
  const creditedClosedCommission = closedDealsCount.reduce(
    (s, d) => s + commissionFor(d),
    0,
  );
  const avgClosedComm =
    creditedClosedDealCount > 0
      ? creditedClosedCommission / creditedClosedDealCount
      : 0;

  if (!teamView && !selectedUser) return <div></div>;

  const initials = teamView ? "TE" : getInitials(selectedUser);
  const avatarBg = teamView ? "bg-[#1E293B]" : getAvatarClass(selectedUser);
  const roleLabel = teamView
    ? "Team"
    : selectedUser.role === "Admin"
      ? "Team Lead"
      : selectedUser.role === "Employee"
        ? "Agent"
        : selectedUser.role || "Member";
  const displayName = teamView
    ? "Team Edelstein"
    : `${selectedUser.firstname} ${selectedUser.lastname}`;
  const displayMeta = teamView
    ? `${teamMembers.length} agents · Coldwell Banker`
    : selectedUser.email || "Team Edelstein";
  const firstNameSafe = teamView
    ? "Team"
    : selectedUser.firstname || "Agent";
  // Whose book a credited figure belongs to, for labels that have to say so.
  const creditedTo = teamView ? "the team" : firstNameSafe;
  const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

  // The roster stacks the two axes inside one cell: volume and deal counts
  // follow the agent CREDITED with the deal, while GCI follows the money and
  // so reaches every deal that pays him, teammates' deals included. Stacked
  // with nothing between them, the pair reads like a commission rate — which
  // it is not, because the two halves are not the same deals. Each GCI figure
  // now says how far it reaches.
  const GCI_SCOPE_CLOSED =
    "Every closed deal that paid this agent, including deals credited to a teammate. The volume and deal counts beside it cover only the deals credited to this agent, so the two do not divide into a commission rate.";
  const GCI_SCOPE_PENDING =
    "Every pending deal that pays this agent, including deals credited to a teammate. The volume and deal counts beside it cover only the deals credited to this agent, so the two do not divide into a commission rate.";
  // Every "% YoY" badge on this page covers the same stretch of the calendar
  // on both sides of the division.
  const YOY_PERIOD_NOTE = `Compared against ${priorYear} up to this same date, not against the whole of ${priorYear}.`;
  const isEmployeeView = !teamView && selectedUser?.role === "Employee";

  // Goal Pacing panel — defined here so the same JSX renders in both the slim
  // dashboard-embedded view AND the full team-detail view. Reads closure
  // variables (volumeGoal, ytdVolume, projectedPct, etc.) from this scope.
  const goalPacingPanel = (
    <PanelShell
      icon={<Target size={14} strokeWidth={2} />}
      title="Goal Pacing"
      subtitle={
        volumeGoal > 0
          ? `Tracking against $${formatNumber(volumeGoal)} annual volume target`
          : "No annual volume goal set for this user"
      }
      right={
        volumeGoal > 0 && (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11.5px] font-semibold ${
              isOnPace
                ? "bg-green-100 text-green-800"
                : "bg-amber-100 text-amber-800"
            }`}
          >
            {isOnPace ? (
              <CheckCircle size={11} strokeWidth={2.5} />
            ) : (
              <AlertTriangle size={11} strokeWidth={2.5} />
            )}
            {isOnPace ? "On Pace" : "Behind"}
          </span>
        )
      }
    >
      {volumeGoal > 0 ? (
        <>
          <div className="text-[32px] font-bold text-slate-900 -tracking-[0.025em] leading-none flex items-baseline gap-2">
            <span className="text-gray-400 font-medium">$</span>
            {formatNumber(displayVolume)}
            <span className="text-[13px] text-gray-500 font-medium">
              of ${formatNumber(volumeGoal)} goal
            </span>
          </div>
          <div className="my-2 mb-[18px] text-[12.5px] text-gray-500">
            Projected to finish at{" "}
            <strong className="text-slate-900 font-semibold">
              ${formatNumber(projectedYearEnd)}
            </strong>{" "}
            · {projectedPct.toFixed(1)}% of goal at current run rate
          </div>

          {/* Pacing bar — green segment = closed volume (where you are),
              yellow segment = pending volume stacked on top (only shown
              when "Include pending" toggle is on). */}
          <div className="relative h-8 mb-3">
            <div className="absolute top-3 left-0 right-0 h-2 bg-slate-100 rounded overflow-hidden flex">
              <div
                className="h-full bg-emerald-500"
                title={`Closed: $${formatNumber(ytdVolume)}`}
                style={{ width: `${Math.min(closedPacePct, 100)}%` }}
              />
              {includePending && (
                <div
                  className="h-full bg-amber-400"
                  title={`Pending: $${formatNumber(ytdPendingVolume)}`}
                  style={{
                    width: `${Math.min(
                      volumeGoal > 0
                        ? (ytdPendingVolume / volumeGoal) * 100
                        : 0,
                      Math.max(0, 100 - Math.min(closedPacePct, 100)),
                    )}%`,
                  }}
                />
              )}
            </div>
            <div
              className="absolute top-1.5 w-0.5 h-5 bg-green-600"
              style={{ left: `${Math.min(expectedPacePct, 100)}%` }}
            >
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-green-600 text-white px-1.5 py-px rounded text-[9.5px] font-bold uppercase tracking-wider whitespace-nowrap">
                On pace
              </div>
            </div>
            {/* The marker labels the end of the GREEN segment, so both halves
               of it are closed volume. It used to print closed dollars beside
               volumePacePct, which counts pending too once the toggle is on. */}
            <div
              className="absolute -bottom-4 -translate-x-1/2 text-[10.5px] font-semibold text-slate-900"
              style={{ left: `${Math.min(closedPacePct, 100)}%` }}
            >
              ${formatNumber(ytdVolume)} · {closedPacePct.toFixed(0)}%
            </div>
            <div className="absolute right-0 -bottom-4 text-[10.5px] font-medium text-gray-500">
              ${formatNumber(volumeGoal)}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 mt-[18px] border border-black/[0.04] rounded-lg overflow-hidden">
            <div className="p-[14px_16px] bg-gray-50 md:border-r border-black/[0.04]">
              <div className="text-xs text-gray-500 font-medium">
                Need to Hit Goal
              </div>
              <div className="text-[18px] font-semibold text-slate-900 mt-1 flex items-baseline">
                <span className="text-gray-400 font-medium">$</span>
                {formatNumber(needPerMonth)}
                <span className="text-gray-400 text-[13px] font-medium ml-1">
                  / mo
                </span>
              </div>
              <div className="text-[11.5px] text-gray-500 mt-1.5">
                Across remaining {monthsRemaining.toFixed(0)} months
              </div>
            </div>
            <div className="p-[14px_16px] bg-gray-50 md:border-r border-black/[0.04]">
              <div className="text-xs text-gray-500 font-medium">
                Current Run Rate
              </div>
              <div className="text-[18px] font-semibold text-slate-900 mt-1 flex items-baseline">
                <span className="text-gray-400 font-medium">$</span>
                {formatNumber(runRatePerMonth)}
                <span className="text-gray-400 text-[13px] font-medium ml-1">
                  / mo
                </span>
              </div>
              <div className="text-[11.5px] text-gray-500 mt-1.5">
                YTD average across {monthsElapsed.toFixed(1)} months
              </div>
            </div>
            <div className="p-[14px_16px] bg-gray-50">
              <div className="text-xs text-gray-500 font-medium">
                Days Remaining
              </div>
              <div className="text-[18px] font-semibold text-slate-900 mt-1 flex items-baseline">
                {daysRemaining}
                <span className="text-gray-400 text-[13px] font-medium ml-1">
                  days
                </span>
              </div>
              <div className="text-[11.5px] text-gray-500 mt-1.5">
                Until end of {selectedYear}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-[13px] text-gray-500 py-3">
          Set an annual volume goal for {firstNameSafe} to track pacing
          against target.
        </div>
      )}
    </PanelShell>
  );

  // ───── DASHBOARD-EMBEDDED VARIANT ─────
  // Slim version for the home dashboard hero. No page header / identity card —
  // those are already shown by the dashboard. Goal Pacing + Upcoming Closings
  // and (optionally) the monthly breakdown table.
  if (dashboardview) {
    return (
      <div className="space-y-4">
        {/* Goal Pacing intentionally omitted here — the navy DashboardStat
           hero on the dashboard already covers it. Goal Pacing still shows
           in the full team-detail view below. */}
        {user?.role !== "Accountant" && (
          <UpcomingClosingsPanel
            title="Upcoming Closings"
            deals={upcomingDeals}
            totalCommission={upcomingCommission}
            today={today}
            commissionForDeal={commissionFor}
          />
        )}
        {(selectedUser.role === "Admin" ||
          selectedUser.role === "Accountant") && (
          <UpcomingClosingsPanel
            title="Upcoming Team Closings"
            deals={upcomingTeamDeals}
            totalCommission={upcomingTeamCommission}
            today={today}
            commissionForDeal={(d) =>
              Number(d.takehomecommissionamount || 0)
            }
          />
        )}
        {isShowMonthly && (
          <MonthlyTablePanel
            monthlyData={monthlyData}
            currentMonth={currentMonth}
            selectedUser={selectedUser}
            gciGoal={gciGoal}
            PendingDealsCount={PendingDealsCount}
            closedDealsCount={closedDealsCount}
            ytdPendingVolume={ytdPendingVolume}
            ytdVolume={ytdVolume}
            closedCommissionTotal={closedCommissionTotal}
            pendingCommissionTotal={pendingCommissionTotal}
            closedPaidDealsYTD={closedPaidDealsYTD}
            pendingPaidDealsYTD={pendingPaidDealsYTD}
            creditedTo={creditedTo}
            TotalPct={TotalPct}
            goalBasisNote={GOAL_BASIS_NOTE}
            filterYear={filterYear}
            yearDropdownVisible={yearDropdownVisible}
            setYearDropdownVisible={setYearDropdownVisible}
            setFilterYear={setFilterYear}
            setSelectedYear={setSelectedYear}
            setstatspopup={setstatspopup}
            setselectedstat={setselectedstat}
            setstatsdata={setstatsdata}
            dashboardview={true}
          />
        )}
        {statspopup && (
          <StatDetails
            Data={statsdata}
            selectedstat={selectedstat}
            onClose={() => setstatspopup(false)}
            isStatCard={true}
            statUserId={id}
            role={selectedUser?.role}
            onSubmit={(data) => console.log(data)}
          />
        )}
      </div>
    );
  }

  // ───── FULL PAGE VARIANT ─────
  // When embedded on the dashboard (hideHeader=true), use a light top
  // padding — enough to breathe under the PageHeader, but not the full
  // pt-7 the standalone team-detail page uses.
  const topPad = hideHeader ? "pt-4" : "pt-7";
  const container = isWidth
    ? `max-w-[1380px] mx-auto px-9 ${topPad} pb-20`
    : `${topPad} pb-20`;

  return (
    <div className={hideHeader ? "" : "bg-slate-100 min-h-screen"}>
      {/* ============ NAVY PAGE HEADER ============ */}
      {!hideHeader && (
      <div className="bg-[#1E293B] text-white px-9 py-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">
            {teamView ? "Team Performance" : `${displayName} — Performance`}
          </h1>
          <div className="text-xs text-white/55 mt-1 font-medium">
            <Link
              to="/dashboard"
              className="text-white/55 hover:text-white no-underline"
            >
              Dashboard
            </Link>
            <span className="opacity-40"> · </span>
            {teamView ? (
              <span className="text-white font-semibold">Team Performance</span>
            ) : (
              <>
                <Link
                  to="/dashboard/team-management"
                  className="text-white/55 hover:text-white no-underline"
                >
                  Team Performance
                </Link>
                <span className="opacity-40"> · </span>
                <span className="text-white font-semibold">{displayName}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!teamView && (
            <Link
              to="/dashboard/team-management"
              className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 border border-white/15 text-white px-3 py-1.5 rounded-md text-[13px] font-medium no-underline"
            >
              <ChevronLeft size={14} />
              Back to Team
            </Link>
          )}
          <AgentPicker
            visible={agentDropdownVisible}
            setVisible={setAgentDropdownVisible}
            teamView={teamView}
            currentId={selectedUser?.id}
            displayName={displayName}
            teamMembers={teamMembers}
            onSelect={(target) => {
              setAgentDropdownVisible(false);
              if (target === "all") {
                navigate("/dashboard/team-management");
              } else {
                navigate(`/dashboard/team-management/${target}`);
              }
            }}
          />
          <div className="relative">
            <button
              onClick={() => setYearDropdownVisible(!yearDropdownVisible)}
              className="bg-white/10 hover:bg-white/15 border border-white/15 text-white px-3 py-1.5 rounded-md text-[13px] font-medium cursor-pointer"
            >
              {filterYear} · YTD
            </button>
            <YearFilterDropdown
              visible={yearDropdownVisible}
              onSelect={(year) => {
                setFilterYear(year);
                setSelectedYear(Number(year));
                setYearDropdownVisible(false);
              }}
              activateOutsideClick={() => setYearDropdownVisible(false)}
              style={{ top: "40px", right: "0px" }}
            />
          </div>
        </div>
      </div>
      )}

      <div className={container}>
        {/* ============ IDENTITY CARD ============ */}
        <div className="bg-white rounded-xl shadow-sm border border-black/[0.04] px-7 py-6 mb-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div
              className={`w-14 h-14 rounded-full ${avatarBg} text-white flex items-center justify-center font-semibold text-[17px] tracking-wide flex-shrink-0 overflow-hidden`}
            >
              {!teamView && selectedUser?.profilepicture ? (
                <img
                  src={selectedUser.profilepicture}
                  alt={displayName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                initials
              )}
            </div>
            <div>
              <div className="text-[22px] font-semibold text-slate-900 tracking-tight">
                {displayName}
              </div>
              <div className="mt-1 text-[13px] text-gray-500 flex items-center gap-2">
                <span
                  className={`${
                    teamView || selectedUser?.role === "Admin"
                      ? "bg-[#1E293B]"
                      : "bg-gray-500"
                  } text-white px-2 py-[3px] text-[10.5px] font-semibold rounded uppercase tracking-wider`}
                >
                  {roleLabel}
                </span>
                <span className="w-[3px] h-[3px] rounded-full bg-slate-300"></span>
                <span>{displayMeta}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <span
              className={`inline-flex items-center rounded-full px-4 py-1.5 text-[13px] font-medium ${
                volumeGoal > 0
                  ? isOnPace
                    ? "bg-green-100 text-green-800"
                    : "bg-amber-100 text-amber-800"
                  : "bg-slate-100 text-slate-700"
              }`}
            >
              {volumeGoal > 0
                ? `${isOnPace ? "On Pace" : "Behind Pace"} · ${projectedPct.toFixed(1)}% projected`
                : "No goal set"}
            </span>
            {volumeGoal > 0 && (
              <button
                type="button"
                onClick={() => setIncludePending((v) => !v)}
                title={
                  includePending
                    ? "Showing closed + pending volume. Click to switch to closed only."
                    : "Showing closed volume only. Click to include pending."
                }
                className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-slate-800"
              >
                <span
                  className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                    includePending ? "bg-[#1E293B]" : "bg-slate-300"
                  }`}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                      includePending ? "translate-x-3.5" : "translate-x-0.5"
                    }`}
                  />
                </span>
                <span>Include pending</span>
              </button>
            )}
          </div>
        </div>

        {topSlot}

        {/* ============ HERO STATS ============ */}
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr_1fr] gap-0 border border-black/[0.04] rounded-xl bg-white overflow-hidden shadow-sm mb-5">
          <div className="p-[22px_26px] lg:border-r border-black/[0.06]">
            <div className="text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold mb-2.5 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-600 shadow-[0_0_0_3px_rgba(22,163,74,0.15)]"></span>
              Year to date · {selectedYear}
            </div>
            <div className="text-4xl font-bold text-slate-900 -tracking-[0.02em] leading-none flex items-baseline gap-2.5">
              <div>
                <span className="text-gray-400 font-medium text-[0.65em]">
                  $
                </span>
                {formatNumber(displayVolume)}
              </div>
              {/* Same-period: this year to today against last year to the
                 same date. Measuring a part-year against a whole year is the
                 same mistake as a paid numerator over a credited denominator,
                 and it made a flat year look like a collapse. */}
              {volumeYoy !== null && (
                <span
                  title={YOY_PERIOD_NOTE}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11.5px] font-semibold ${
                    volumeYoy >= 0
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {volumeYoy >= 0 ? (
                    <ArrowUp size={11} strokeWidth={2.5} />
                  ) : (
                    <TrendingDown size={11} strokeWidth={2.5} />
                  )}
                  {Math.abs(volumeYoy).toFixed(1)}% YoY
                </span>
              )}
            </div>
            <div className="mt-2 text-[12.5px] text-gray-500">
              {includePending
                ? `Closed + pending volume across ${displayUnits} deals`
                : `Closed transaction volume across ${closedDealsCount.length} deals`}
            </div>
            {volumeGoal > 0 && (
              <div className="mt-2.5">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-semibold bg-slate-100 text-slate-700">
                  {Math.round(volumePacePct)}% of goal
                </span>
              </div>
            )}
          </div>

          <div
            className="p-[22px_26px] lg:border-r border-black/[0.06]"
            title={`Closed Commission is every dollar ${creditedTo} was paid this year, including slices earned on deals credited to somebody else. The average underneath covers only the deals credited to ${creditedTo}, so it will not multiply back out to this figure when a teammate's deal also paid him.`}
          >
            <div className="text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold mb-2.5">
              Closed Commission
            </div>
            <div className="text-[26px] font-bold text-slate-900 -tracking-[0.015em] leading-none flex items-baseline gap-2">
              <div>
                <span className="text-gray-400 font-medium">$</span>
                {formatNumber(closedCommissionTotal)}
              </div>
              {closedGciYoy !== null && (
                <span
                  title={YOY_PERIOD_NOTE}
                  className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10.5px] font-semibold ${
                    closedGciYoy >= 0
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {closedGciYoy >= 0 ? "↑" : "↓"}
                  {Math.abs(closedGciYoy).toFixed(1)}% YoY
                </span>
              )}
            </div>
            {/* The average names the deals it is an average OF. They are the
               deals CREDITED to him, which is a narrower list than the headline
               above whenever a teammate's deal pays him a slice, so the line
               has to say which list it means. */}
            <div className="mt-1.5 text-[11.5px] text-gray-500">
              ${formatNumber(avgClosedComm)} avg across{" "}
              {plural(creditedClosedDealCount, "deal")} credited to {creditedTo}
            </div>
            {closedGciPacePct !== null && (
              <div className="mt-2.5">
                <span
                  title={GOAL_BASIS_NOTE}
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-semibold bg-slate-100 text-slate-700"
                >
                  {Math.round(closedGciPacePct)}% of goal
                </span>
              </div>
            )}
          </div>

          <div
            className="p-[22px_26px]"
            title={`Pending Commission is every dollar ${creditedTo} stands to be paid on deals closing in ${selectedYear}, including slices earned on deals credited to somebody else. The count underneath is units, so it covers only the deals credited to ${creditedTo}.`}
          >
            <div className="text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold mb-2.5">
              Pending Commission
            </div>
            <div className="text-[26px] font-bold text-amber-700 -tracking-[0.015em] leading-none">
              <span className="text-amber-600 font-medium">$</span>
              {formatNumber(pendingCommissionTotal)}
            </div>
            <div className="mt-1.5 text-[11.5px] text-gray-500">
              {/* Units, not dollars: the deals CREDITED to this person, the
                 same list the YTD Pending Volume tile counts. The dollar
                 figure above it is wider on purpose, because it includes
                 slices paid out of deals credited to somebody else — so this
                 line names whose deals it is counting, and it no longer
                 borrows the Upcoming Closings panel's wording for a number
                 that panel works out a different way. */}
              {plural(PendingDealsCount.length, "deal")} credited to{" "}
              {creditedTo}, closing in {selectedYear}
            </div>
          </div>
        </div>

        {/* ============ KPI MINI-CARDS ============
            Through {today.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            · {selectedYear} vs {priorYear}. The YoY % and "of goal" pills
            on the tiles below absorbed what used to be a separate "Year
            over Year vs Goal" row so the same numbers don't appear twice. */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
          <MiniCard
            label="YTD Pending Volume"
            value={`$${formatNumber(ytdPendingVolume)}`}
            sub={`${PendingDealsCount.length} deals`}
            onClick={() => {
              setstatspopup(true);
              setselectedstat(`Pending volume · deals credited to ${creditedTo}`);
              setstatsdata(PendingDealsCount);
            }}
          />
          <MiniCard
            label="Net YTD Volume"
            value={`$${formatNumber(ytdnetVolume)}`}
            sub="Closed + pending"
            onClick={() => {
              setstatspopup(true);
              setselectedstat(
                `Closed + pending volume · deals credited to ${creditedTo}`,
              );
              // Closed + pending, the two lists this tile's figure is the sum
              // of. It used to open AllDealsCount, which carries no stage
              // filter at all, so the drawer also listed cancelled and expired
              // deals that contribute nothing to the total above it.
              setstatsdata([...closedDealsCount, ...PendingDealsCount]);
            }}
          />
          <MiniCard
            label={includePending ? "Deals" : "Closed Deals"}
            value={`${displayUnits}`}
            yoyPct={unitsYoy}
            pacePct={unitsPacePct}
            onClick={() => {
              setstatspopup(true);
              setselectedstat(
                includePending
                  ? `Closed + pending deals credited to ${creditedTo}`
                  : `Closed deals credited to ${creditedTo}`,
              );
              setstatsdata(
                includePending
                  ? [...closedDealsCount, ...PendingDealsCount]
                  : closedDealsCount,
              );
            }}
          />
          <MiniCard
            label="Net Commission"
            value={`$${formatNumber(netCommissionTotal)}`}
            sub="Closed + pending"
            onClick={() => {
              setstatspopup(true);
              setselectedstat(
                `Closed + pending deals that paid ${creditedTo}`,
              );
              setstatsdata(netCommissionDealsYTD);
            }}
          />
          <MiniCard
            label="Avg Sale Price"
            value={`$${formatNumber(displayAvg)}`}
            yoyPct={avgYoy}
          />
        </div>

        {/* ============ GOAL PACING ============ */}
        {goalPacingPanel}

        {/* ============ ACTIVE LISTINGS ============ */}
        {/* On-market only (Henry, 2026-08-30): Active + Coming Soon. Pre-market
            homes aren't listed yet — they show in Pipeline Distribution, not
            here. */}
        <PanelShell
          icon={<Home size={14} strokeWidth={2} />}
          title="Active Listings"
          subtitle={
            activeListings.length === 0 && comingSoonListings.length === 0
              ? "No active or coming-soon listings"
              : `${activeListings.length} active · ${comingSoonListings.length} coming soon`
          }
        >
          {activeListings.length === 0 && comingSoonListings.length === 0 ? (
            <div className="text-[12.5px] text-gray-500 py-2">
              Listings will appear here when their stage moves to Active or
              Coming Soon.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {[
                ...activeListings.map((d) => ({ ...d, _stageLabel: "Active" })),
                ...comingSoonListings.map((d) => ({
                  ...d,
                  _stageLabel: "Coming Soon",
                })),
              ]
                .sort((a, b) =>
                  (a.propertyaddress || "").localeCompare(
                    b.propertyaddress || "",
                  ),
                )
                .map((d) => {
                  const price = Number(
                    d.listPrice || d.suggestedPrice || d.offerPrice || 0,
                  );
                  const stageStyle =
                    d._stageLabel === "Active"
                      ? "bg-emerald-100 text-emerald-700"
                      : d._stageLabel === "Coming Soon"
                        ? "bg-slate-100 text-slate-700"
                        : "bg-blue-100 text-blue-700";
                  return (
                    <li
                      key={d.createdat || d.id}
                      onClick={() =>
                        navigate(`/dashboard/deals-detail/${d.createdat}`)
                      }
                      className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-slate-100 hover:border-slate-300 hover:bg-slate-50 cursor-pointer transition"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {d.propertyaddress || "Untitled listing"}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
                          <span
                            className={`px-1.5 py-px rounded text-[10px] font-semibold uppercase tracking-wider ${stageStyle}`}
                          >
                            {d._stageLabel}
                          </span>
                          {d.closingdate && (
                            <span>Closing {formatDate(d.closingdate)}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-semibold text-slate-900">
                          {price > 0
                            ? `$${price.toLocaleString("en-US")}`
                            : "—"}
                        </div>
                      </div>
                    </li>
                  );
                })}
            </ul>
          )}
        </PanelShell>

        {/* ============ TEAM ROSTER (team view only) ============ */}
        {teamView && (
          <PanelShell
            icon={<Users size={14} strokeWidth={2} />}
            title="Team Roster"
            subtitle="Click an agent to drill into their personal view · Volume and deal counts follow the agent credited with each deal; GCI follows the money, so it also covers slices earned on teammates' deals"
            right={
              <RosterSeg
                options={[
                  { id: "volume", label: "Volume" },
                  { id: "gci", label: "GCI" },
                ]}
                value={rosterMetric}
                onChange={setRosterMetric}
                ariaLabel="Measure the roster in volume or GCI"
              />
            }
          >
            {/* Rank-by control + team totals for the chosen measure */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3 pb-4">
              <span className="text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold">
                Rank by
              </span>
              <RosterSeg
                options={[
                  { id: "closed", label: "Closed" },
                  { id: "pending", label: "Pending" },
                  { id: "total", label: "Closed + Pending" },
                ]}
                value={rosterMode}
                onChange={setRosterMode}
                ariaLabel="Rank the roster by closed, pending, or both"
              />
              {/* Same two axes as the rows below, added up. In Volume mode the
                 dollars and the count are the same deals. In GCI mode the
                 dollars follow the money and the count follows the credit, so
                 the pair is not a commission rate and the tooltip says so
                 rather than leaving a reader to divide one by the other. */}
              <div className="flex items-center gap-4 ml-auto">
                <RosterTotal
                  label="Team closed"
                  value={rosterTotals.closed}
                  sub={`${rosterTotals.deals} deals`}
                  title={
                    rosterMetric === "gci"
                      ? "Every closed deal that paid somebody on this roster, added up. The count beneath is units, so it covers only the deals credited to a roster member."
                      : "Sale price of the closed deals credited to roster members, and the number of those deals."
                  }
                />
                <RosterTotal
                  label="Team pending"
                  value={rosterTotals.pending}
                  sub={`${rosterTotals.active} pending deals`}
                  title={
                    rosterMetric === "gci"
                      ? "Every pending deal that pays somebody on this roster, added up. The count beneath is units, so it covers only the deals credited to a roster member."
                      : "Sale price of the pending deals credited to roster members, and the number of those deals."
                  }
                  accent
                />
                <RosterTotal
                  label="Combined"
                  value={rosterTotals.closed + rosterTotals.pending}
                  sub="closed + pending"
                />
              </div>
            </div>

            <div className="grid grid-cols-[46px_1.3fr_0.85fr_0.9fr_0.85fr_0.7fr_24px] gap-3 items-center pb-3 border-b border-gray-200">
              <span></span>
              <span className="pl-1 text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold">
                Agent
              </span>
              <RosterColHead
                label={rosterMetric === "gci" ? "Closed GCI" : "Closed Volume"}
                active={rosterMode === "closed"}
                onClick={() => setRosterMode("closed")}
              />
              <span className="text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold">
                Deals (All)
              </span>
              <RosterColHead
                label="Pending"
                active={rosterMode === "pending"}
                onClick={() => setRosterMode("pending")}
              />
              <RosterColHead
                label="Total"
                active={rosterMode === "total"}
                onClick={() => setRosterMode("total")}
              />
              <span></span>
            </div>
            {rankedRoster.length === 0 ? (
              <div className="py-8 text-center text-gray-400 text-[13px]">
                No team members found
              </div>
            ) : (
              rankedRoster.map((row, i) => {
                const agentInitials = getInitials(row.user);
                const agentAvatar = getAvatarClass(row.user);
                const subRole =
                  row.user.role === "Admin"
                    ? "Team Lead"
                    : row.user.role === "Employee"
                      ? "Agent"
                      : row.user.role || "Member";
                return (
                  <Link
                    key={row.user.id}
                    to={`/dashboard/team-management/${row.user.id}`}
                    className="no-underline"
                  >
                    <div className="relative grid grid-cols-[46px_1.3fr_0.85fr_0.9fr_0.85fr_0.7fr_24px] gap-3 items-center py-3.5 border-b border-slate-100 last:border-b-0 cursor-pointer hover:bg-gray-50 group transition">
                      {/* Share of the leader, on whatever we're ranking by */}
                      <span
                        aria-hidden="true"
                        className={`absolute left-0 bottom-0 h-[2px] transition-all duration-500 ${
                          rosterMode === "pending"
                            ? "bg-sky-400/60"
                            : "bg-slate-300"
                        }`}
                        style={{ width: `${(row.share * 100).toFixed(2)}%` }}
                      />
                      <div className="flex items-center gap-1.5">
                        {i < 3 && row.rankValue > 0 ? (
                          <span
                            className={`w-[21px] h-[21px] rounded-full inline-flex items-center justify-center text-white text-[10.5px] font-bold flex-shrink-0 ${MEDAL_CLASS[i]}`}
                          >
                            {i + 1}
                          </span>
                        ) : (
                          <span className="w-[21px] text-[13px] font-semibold text-right text-gray-400">
                            {i + 1}
                          </span>
                        )}
                        <RosterMove delta={row.delta} />
                      </div>
                      <div className="flex items-center gap-3 min-w-0 pl-1">
                        <div
                          className={`w-9 h-9 rounded-full ${agentAvatar} text-white flex items-center justify-center font-semibold text-[12.5px] tracking-wide flex-shrink-0 overflow-hidden`}
                        >
                          {row.user?.profilepicture ? (
                            <img
                              src={row.user.profilepicture}
                              alt=""
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            agentInitials
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 leading-tight truncate">
                            {row.user.firstname} {row.user.lastname}
                          </div>
                          <div className="text-[11.5px] text-gray-500 mt-0.5 truncate">
                            {subRole}
                          </div>
                        </div>
                      </div>
                      <div>
                        <div
                          title={
                            rosterMetric === "gci"
                              ? GCI_SCOPE_CLOSED
                              : "Sale price of the closed deals credited to this agent."
                          }
                          className={`text-sm text-slate-900 ${
                            rosterMode === "closed"
                              ? "font-bold"
                              : "font-semibold"
                          }`}
                        >
                          <span className="text-gray-400 font-medium">$</span>
                          {formatNumber(
                            rosterMetric === "gci" ? row.gci : row.volume,
                          )}
                        </div>
                        <div
                          title={
                            rosterMetric === "gci"
                              ? "Closed deals credited to this agent."
                              : GCI_SCOPE_CLOSED
                          }
                          className="text-[11.5px] text-gray-500 mt-0.5 font-medium"
                        >
                          {rosterMetric === "gci"
                            ? `${row.dealsClosed} closed`
                            : `$${formatNumber(row.gci)} GCI · all deals`}
                        </div>
                      </div>
                      <button
                        type="button"
                        title="See these deals"
                        onClick={(e) => {
                          // The row is a Link to the agent's page — the Deals
                          // number opens the list instead of navigating.
                          e.preventDefault();
                          e.stopPropagation();
                          setRosterDealsFor(row);
                        }}
                        className="text-left -m-1.5 p-1.5 rounded-lg hover:bg-slate-100 transition"
                      >
                        <div className="text-sm font-semibold text-slate-900 underline decoration-slate-300 decoration-dotted underline-offset-2">
                          {row.dealsClosed + row.dealsActive}
                        </div>
                        <div className="text-[11.5px] text-gray-500 mt-0.5 font-medium leading-snug">
                          <div>{row.dealsClosed} closed</div>
                          <div>{row.dealsActive} pending</div>
                        </div>
                      </button>
                      <div>
                        <div
                          title={
                            rosterMetric === "gci"
                              ? GCI_SCOPE_PENDING
                              : "Sale price of the pending deals credited to this agent."
                          }
                          className={`text-sm text-sky-700 ${
                            rosterMode === "pending"
                              ? "font-bold"
                              : "font-semibold"
                          }`}
                        >
                          <span className="text-sky-600/50 font-medium">$</span>
                          {formatNumber(
                            rosterMetric === "gci"
                              ? row.pendingComm
                              : row.pendingVolume,
                          )}
                        </div>
                        <div
                          title={
                            rosterMetric === "gci"
                              ? "Pending deals credited to this agent."
                              : GCI_SCOPE_PENDING
                          }
                          className="text-[11.5px] text-gray-500 mt-0.5 font-medium"
                        >
                          {rosterMetric === "gci"
                            ? `${row.dealsActive} pending`
                            : `$${formatNumber(row.pendingComm)} GCI · all pending`}
                        </div>
                      </div>
                      <div>
                        <div
                          className={`text-sm text-slate-900 ${
                            rosterMode === "total" ? "font-bold" : "font-semibold"
                          }`}
                        >
                          <span className="text-gray-400 font-medium">$</span>
                          {formatNumber(
                            rosterMetric === "gci"
                              ? row.gci + row.pendingComm
                              : row.volume + row.pendingVolume,
                          )}
                        </div>
                        <div className="text-[11.5px] text-gray-500 mt-0.5 font-medium">
                          combined
                        </div>
                      </div>
                      <ChevronRight
                        size={16}
                        className="text-slate-300 group-hover:text-[#1E293B] group-hover:translate-x-0.5 transition"
                      />
                    </div>
                  </Link>
                );
              })
            )}
            {rosterDealsFor && (
              <StatDealsModal
                title={`${rosterDealsFor.user.firstname} ${rosterDealsFor.user.lastname} · Deals`}
                deals={[
                  ...rosterDealsFor.closedDeals,
                  ...rosterDealsFor.pendingDeals,
                ]}
                amountOf={(d) => Number(d.offerPrice || 0)}
                onClose={() => setRosterDealsFor(null)}
              />
            )}
          </PanelShell>
        )}

        {/* ============ PIPELINE DISTRIBUTION ============ */}
        <PanelShell
          icon={<Filter size={14} strokeWidth={2} />}
          title="Pipeline Distribution"
          subtitle={`${openPipelineCount} open deals across the pipeline`}
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <MiniCard
              label="Open Pipeline"
              value={`$${formatNumber(openPipelineVolume)}`}
            />
            <MiniCard
              label="Deals in Flight"
              value={`${openPipelineCount}`}
            />
            {/* Open Pipeline and Deals in Flight are the credited book; this
               one is dollars, so it is wider than those two. Says so, rather
               than sitting in the same row of cards looking like the
               commission on exactly those deals. */}
            <MiniCard
              label="Expected GCI"
              value={`$${formatNumber(expectedOpenGci)}`}
              sub="Every open deal that pays them"
            />
            <MiniCard
              label="Stages With Activity"
              value={`${pipelineRows.filter((r) => r.count > 0).length}`}
            />
          </div>

          <div className="flex items-center gap-3 mt-[22px] mb-3.5">
            <span className="text-[11px] font-semibold text-[#1E293B] tracking-wider uppercase">
              Stage Breakdown
            </span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {pipelineRows.map((s) => {
            // No text inside the bar — a one-deal bar can't hold a label
            // (the old "Coming Soon" sliver wrapped its text outside the
            // fill). The count column on the right says the number instead.
            const widthPct =
              maxPipelineCount > 0
                ? Math.max(2.5, (s.count / maxPipelineCount) * 100)
                : 0;
            return (
              <div
                key={s.key}
                title={`${s.label}: ${s.count} ${s.count === 1 ? "deal" : "deals"}${s.volume ? ` · $${formatNumber(s.volume)}` : ""}`}
                className="grid grid-cols-[130px_1fr_90px_70px] items-center gap-3.5 py-2.5 rounded-lg hover:bg-slate-50 transition"
              >
                <div className="flex items-center gap-2.5 text-[13px] font-medium text-slate-800">
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ background: s.color }}
                  ></span>
                  {s.label}
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  {s.count > 0 && (
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${widthPct}%`,
                        background: s.color,
                      }}
                    ></div>
                  )}
                </div>
                <div className="text-right text-[13px] font-semibold text-slate-900 tabular-nums">
                  {s.volume > 0 ? (
                    <>
                      <span className="text-gray-400 font-medium">$</span>
                      {formatNumber(s.volume)}
                    </>
                  ) : (
                    <span className="text-gray-300 font-medium">—</span>
                  )}
                </div>
                <div className="text-right text-xs text-gray-500 font-medium tabular-nums">
                  <strong className="text-slate-900 font-semibold mr-0.5">
                    {s.count}
                  </strong>
                  {s.count === 1 ? "deal" : "deals"}
                </div>
              </div>
            );
          })}
        </PanelShell>

        {/* ============ CYCLE TIME + CONVERSION (shells) ============ */}
        {/* Hidden for Employees — these team-wide analytics aren't useful
            to a single agent and clutter their dashboard. Visible to
            Admin / Supervisor / Accountant. */}
        {user?.role !== "Employee" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
          <PanelShell
            icon={<Clock size={14} strokeWidth={2} />}
            title="Cycle Time Analysis"
            subtitle={`Median time across deal lifecycle · ${selectedYear}`}
          >
            <div className="grid grid-cols-2 gap-0">
              <CycleCell
                label="Lead → Appointment"
                value={
                  cycleLeadToAppt.median != null
                    ? Math.round(cycleLeadToAppt.median)
                    : "—"
                }
                foot={
                  cycleLeadToAppt.sample > 0
                    ? `From ${cycleLeadToAppt.sample} appointment${cycleLeadToAppt.sample === 1 ? "" : "s"}`
                    : "No appointments logged"
                }
              />
              <CycleCell
                label="Listing → Under Contract"
                value={
                  cycleListToUc.median != null
                    ? Math.round(cycleListToUc.median)
                    : "—"
                }
                foot={
                  cycleListToUc.sample > 0
                    ? `From ${cycleListToUc.sample} deal${cycleListToUc.sample === 1 ? "" : "s"}`
                    : "Stamped on future stage flips"
                }
              />
              <CycleCell
                label="Contract → Close"
                value={
                  cycleUcToClose.median != null
                    ? Math.round(cycleUcToClose.median)
                    : "—"
                }
                foot={
                  cycleUcToClose.sample > 0
                    ? `From ${cycleUcToClose.sample} closed deal${cycleUcToClose.sample === 1 ? "" : "s"}`
                    : "Stamped on future stage flips"
                }
              />
              <CycleCell
                label="Lead → Close (Full)"
                value={
                  cycleLeadToClose.median != null
                    ? Math.round(cycleLeadToClose.median)
                    : "—"
                }
                foot={
                  cycleLeadToClose.sample > 0
                    ? `From ${cycleLeadToClose.sample} closed deal${cycleLeadToClose.sample === 1 ? "" : "s"}`
                    : "No closed deals yet"
                }
              />
            </div>
          </PanelShell>

          <PanelShell
            icon={<Filter size={14} strokeWidth={2} />}
            title="Conversion Funnel"
            subtitle={`Seller-side pull-through · ${selectedYear}`}
          >
            <div className="flex items-center justify-between gap-1 py-1">
              <ConvStage num={funnel.cma} label="CMA's" />
              <ConvArrow />
              <ConvStage num={funnel.listed} label="Listed" />
              <ConvArrow />
              <ConvStage num={funnel.uc} label="Under Contract" />
              <ConvArrow />
              <ConvStage num={funnel.closed} label="Closed" emphasize />
            </div>
            <div className="grid grid-cols-4 gap-1 mt-4 pt-4 border-t border-gray-200">
              <ConvRate pct={funnel.cmaToList} label="CMA → List" />
              <ConvRate pct={funnel.listToUc} label="List → UC" />
              <ConvRate pct={funnel.ucToClosed} label="UC → Closed" />
              <ConvRate pct={funnel.overall} label="Overall" />
            </div>
          </PanelShell>
        </div>
        )}

        {/* ============ MONTHLY CHART + UPCOMING CLOSINGS ============ */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5 mb-5">
          <PanelShell
            icon={<BarChart3 size={14} strokeWidth={2} />}
            title={`${teamView ? "Team" : firstNameSafe}${teamView ? "" : "'s"} Monthly Performance`}
            subtitle={`Net commission · ${selectedYear} vs ${priorYear}`}
          >
            <MonthlyChart
              monthlyData={monthlyData}
              priorYearMonthly={priorYearMonthly}
              currentMonth={currentMonth}
              selectedYear={selectedYear}
            />
          </PanelShell>

          {!hideUpcoming && (
            <UpcomingClosingsPanel
              title="Upcoming Closings"
              deals={upcomingDeals}
              totalCommission={upcomingCommission}
              today={today}
              commissionForDeal={commissionFor}
              tight
            />
          )}
        </div>

        {/* ============ LEAD SOURCES ============
           The rows only cover closings that HAVE a lead source recorded, so the
           header counts those against the whole closed year and says so. Worded
           as a plain closings count it read as the whole year and quietly
           disagreed with the Closed Deals tile whenever a deal carried no
           source. */}
        {leadSourceData.length > 0 && (
          <PanelShell
            icon={<Activity size={14} strokeWidth={2} />}
            title="Lead Sources · Closed Transactions"
            subtitle={`YTD ${selectedYear} · ${totalLeadSourceCount} of ${closedDealsCount.length} closings have a source recorded`}
          >
            <div className="grid grid-cols-[110px_1fr_80px_70px] gap-3.5 pb-2.5 border-b border-gray-200 mb-1">
              <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
                Source
              </span>
              <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
                Distribution
              </span>
              <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold text-right">
                Share
              </span>
              <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold text-right">
                Closings
              </span>
            </div>
            {leadSourceData.map((s, i) => {
              const pct =
                totalLeadSourceCount > 0
                  ? Math.round((s.dealClosedCount / totalLeadSourceCount) * 100)
                  : 0;
              const w = (s.dealClosedCount / maxLeadSourceCount) * 100;
              const colorCls =
                LEAD_SOURCE_COLORS[i % LEAD_SOURCE_COLORS.length];
              return (
                <div
                  key={s.leadsourceId}
                  className="grid grid-cols-[110px_1fr_80px_70px] items-center gap-3.5 py-2.5 border-b border-slate-100 last:border-b-0 cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    setstatspopup(true);
                    setselectedstat(
                      `${s.leadsourceName} · closed deals credited to ${creditedTo}`,
                    );
                    setstatsdata(s.deals);
                  }}
                >
                  <div className="text-[13px] font-semibold text-slate-900">
                    {s.leadsourceName}
                    <span className="block text-[11px] font-medium text-gray-500 mt-0.5">
                      {s.leadsourceAbbreviation}
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-sm overflow-hidden">
                    <div
                      className={`h-full rounded-sm transition-all duration-500 ${colorCls}`}
                      style={{ width: `${w}%` }}
                    ></div>
                  </div>
                  <div className="text-right text-xs text-slate-600 font-medium">
                    {pct}%
                  </div>
                  <div className="text-right text-sm font-semibold text-slate-900">
                    {s.dealClosedCount}
                  </div>
                </div>
              );
            })}
          </PanelShell>
        )}

        {/* ============ MONTHLY BREAKDOWN TABLE ============ */}
        {isShowMonthly && (
          <MonthlyTablePanel
            monthlyData={monthlyData}
            currentMonth={currentMonth}
            selectedUser={selectedUser}
            gciGoal={gciGoal}
            PendingDealsCount={PendingDealsCount}
            closedDealsCount={closedDealsCount}
            ytdPendingVolume={ytdPendingVolume}
            ytdVolume={ytdVolume}
            closedCommissionTotal={closedCommissionTotal}
            pendingCommissionTotal={pendingCommissionTotal}
            closedPaidDealsYTD={closedPaidDealsYTD}
            pendingPaidDealsYTD={pendingPaidDealsYTD}
            creditedTo={creditedTo}
            TotalPct={TotalPct}
            goalBasisNote={GOAL_BASIS_NOTE}
            filterYear={filterYear}
            yearDropdownVisible={yearDropdownVisible}
            setYearDropdownVisible={setYearDropdownVisible}
            setFilterYear={setFilterYear}
            setSelectedYear={setSelectedYear}
            setstatspopup={setstatspopup}
            setselectedstat={setselectedstat}
            setstatsdata={setstatsdata}
            dashboardview={false}
          />
        )}
      </div>

      {statspopup && (
        <StatDetails
          Data={statsdata}
          selectedstat={selectedstat}
          onClose={() => setstatspopup(false)}
          isStatCard={true}
          statUserId={id}
          role={selectedUser?.role}
          onSubmit={(data) => console.log(data)}
        />
      )}
    </div>
  );
}

// ============================================================
// SUB-COMPONENTS
// ============================================================

function AgentPicker({
  visible,
  setVisible,
  teamView,
  currentId,
  displayName,
  teamMembers,
  onSelect,
}) {
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setVisible(false);
      }
    };
    if (visible) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [visible, setVisible]);

  const label = teamView ? "All Team" : displayName || "Select Agent";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setVisible(!visible)}
        className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 border border-white/15 text-white px-3 py-1.5 rounded-md text-[13px] font-medium cursor-pointer"
      >
        <Users size={13} />
        {label}
      </button>
      {visible && (
        <div
          className="absolute right-0 top-10 z-20 w-[240px] bg-white border border-gray-300 rounded-md p-2 shadow-lg text-gray-900"
        >
          <div className="max-h-[300px] overflow-y-auto">
            <div
              onClick={() => onSelect("all")}
              className={`py-1.5 px-3 cursor-pointer rounded-md text-[13px] font-semibold ${
                teamView
                  ? "bg-slate-100 text-slate-900"
                  : "text-gray-800 hover:bg-gray-100"
              }`}
            >
              All Team
            </div>
            <div className="border-t border-gray-100 my-1" />
            {teamMembers.map((u) => {
              const isCurrent = !teamView && u.id === currentId;
              return (
                <div
                  key={u.id}
                  onClick={() => onSelect(u.id)}
                  className={`py-1.5 px-3 cursor-pointer rounded-md text-[13px] ${
                    isCurrent
                      ? "bg-slate-100 text-slate-900 font-semibold"
                      : "text-gray-800 hover:bg-gray-100"
                  }`}
                >
                  {u.firstname} {u.lastname}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function YoyVsGoalCard({ label, ytdValue, yoyPct, pacePct }) {
  return (
    <div className="bg-white rounded-xl border border-black/[0.06] shadow-sm p-4">
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-gray-500">
        {label}
      </div>
      <div className="mt-1.5 text-[22px] font-bold text-slate-900 -tracking-[0.01em] leading-none">
        {ytdValue}
      </div>
      <div className="mt-3 flex flex-col gap-1.5">
        <YoyBadge pct={yoyPct} />
        <PaceBadge pct={pacePct} />
      </div>
    </div>
  );
}

function YoyBadge({ pct }) {
  if (pct === null) {
    return (
      <span className="text-[11px] text-gray-400">No prior-year data</span>
    );
  }
  const isUp = pct >= 0;
  const cls = isUp
    ? "text-green-700 bg-green-50"
    : "text-red-700 bg-red-50";
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11.5px] font-semibold px-2 py-0.5 rounded ${cls} w-fit`}
    >
      {isUp ? (
        <ArrowUp size={11} strokeWidth={2.5} />
      ) : (
        <TrendingDown size={11} strokeWidth={2.5} />
      )}
      {Math.abs(pct).toFixed(1)}% vs last yr same date
    </span>
  );
}

function PaceBadge({ pct }) {
  if (pct === null) {
    return (
      <span className="text-[11px] text-gray-400">No goal set</span>
    );
  }
  return (
    <span className="inline-flex items-center text-[11.5px] font-semibold px-2 py-0.5 rounded text-slate-700 bg-slate-100 w-fit">
      {pct.toFixed(0)}% of goal
    </span>
  );
}

// Gold / silver / bronze for the top three of whichever ranking is showing.
const MEDAL_CLASS = [
  "bg-gradient-to-br from-[#D4A017] to-[#A87A0B]",
  "bg-gradient-to-br from-[#A8B2BD] to-[#7C8794]",
  "bg-gradient-to-br from-[#B4703C] to-[#8A5227]",
];

// Segmented control used for both "Rank by" and the volume/GCI measure.
function RosterSeg({ options, value, onChange, ariaLabel }) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex bg-slate-100 border border-slate-200 rounded-lg p-[3px] gap-[2px]"
    >
      {options.map((o) => {
        const on = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(o.id)}
            className={`px-3 py-1.5 rounded-md text-[12.5px] font-semibold whitespace-nowrap transition ${
              on
                ? "bg-[#1E293B] text-white"
                : "text-gray-500 hover:text-slate-900"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// A column header that also acts as the rank-by switch for that column.
function RosterColHead({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left text-[10.5px] uppercase tracking-wider font-semibold transition ${
        active ? "text-[#1E293B]" : "text-gray-500 hover:text-slate-700"
      }`}
    >
      {label}
      {active && <span className="ml-1 text-[9px]">▼</span>}
    </button>
  );
}

// How far this agent moved against the closed ranking everyone already knows.
function RosterMove({ delta }) {
  if (!delta) return <span className="w-0" />;
  const up = delta > 0;
  return (
    <span
      title={`${Math.abs(delta)} ${up ? "up" : "down"} vs. the closed ranking`}
      className={`inline-flex items-center gap-[1px] text-[10px] font-bold px-1 py-[1px] rounded ${
        up ? "text-emerald-700 bg-emerald-50" : "text-amber-700 bg-amber-50"
      }`}
    >
      {up ? <ArrowUp size={9} strokeWidth={3} /> : <ArrowDown size={9} strokeWidth={3} />}
      {Math.abs(delta)}
    </span>
  );
}

function RosterTotal({ label, value, sub, accent, title }) {
  return (
    <div className="hidden md:block" title={title}>
      <div className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
        {label}
      </div>
      <div
        className={`text-[15px] font-bold leading-tight ${
          accent ? "text-sky-700" : "text-slate-900"
        }`}
      >
        <span className="text-gray-400 font-medium">$</span>
        {formatNumber(value)}
      </div>
      <div className="text-[10.5px] text-gray-500 font-medium">{sub}</div>
    </div>
  );
}

function PanelShell({
  icon,
  iconBg = "bg-[#1E293B]",
  title,
  subtitle,
  right,
  tight,
  children,
}) {
  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-black/[0.04] mb-5 ${
        tight ? "p-0" : "p-6"
      }`}
    >
      <div
        className={`flex items-center justify-between border-b border-black/[0.08] ${
          tight ? "px-6 pt-5 pb-4" : "pb-3.5 mb-[18px]"
        }`}
      >
        <div className="flex items-center gap-3">
          <span
            className={`w-7 h-7 ${iconBg} rounded-md inline-flex items-center justify-center flex-shrink-0 text-white`}
          >
            {icon}
          </span>
          <div>
            <div className="text-[15px] font-semibold text-slate-900">
              {title}
            </div>
            {subtitle && (
              <div className="text-[11px] text-gray-500 mt-0.5">{subtitle}</div>
            )}
          </div>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function MiniCard({ label, value, sub, onClick, yoyPct, pacePct }) {
  return (
    <div
      onClick={onClick}
      className={`p-[14px_16px] bg-gray-50 rounded-lg border border-black/[0.03] ${
        onClick ? "cursor-pointer hover:bg-slate-100 hover:border-black/[0.08] transition" : ""
      }`}
    >
      <div className="text-xs text-gray-500 font-medium">{label}</div>
      <div className="text-[18px] font-semibold text-slate-900 mt-1 -tracking-[0.005em]">
        {value}
      </div>
      {(yoyPct !== undefined && yoyPct !== null) || pacePct !== undefined ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {yoyPct !== undefined && yoyPct !== null && (
            <span
              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10.5px] font-semibold ${
                yoyPct >= 0
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {yoyPct >= 0 ? "↑" : "↓"}
              {Math.abs(yoyPct).toFixed(1)}% YoY
            </span>
          )}
          {pacePct !== undefined && pacePct !== null && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-semibold bg-slate-100 text-slate-700">
              {Math.round(pacePct)}% of goal
            </span>
          )}
          {pacePct === undefined && yoyPct === undefined && null}
        </div>
      ) : null}
      {sub && <div className="text-[11.5px] text-gray-500 mt-1.5">{sub}</div>}
    </div>
  );
}

function CycleCell({ label, value = "—", unit = "days", trend, foot }) {
  return (
    <div className="p-[16px_18px] border-r border-slate-100 border-b border-slate-100 last:border-r-0 [&:nth-child(2n)]:border-r-0 [&:nth-child(n+3)]:border-b-0">
      <div className="text-xs text-gray-500 font-medium mb-2">{label}</div>
      <div>
        <span className="text-[26px] font-bold text-slate-900 -tracking-[0.02em] leading-none">
          {value}
        </span>
        <span className="text-xs text-gray-400 font-medium ml-1">{unit}</span>
        {trend && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700 ml-2">
            {trend}
          </span>
        )}
      </div>
      {foot && (
        <div className="mt-1.5 text-[11px] text-gray-500">{foot}</div>
      )}
    </div>
  );
}

function ConvStage({ num, label, emphasize }) {
  return (
    <div className="flex-1 text-center">
      <div
        className={`text-[22px] font-bold -tracking-[0.02em] leading-none ${
          emphasize ? "text-green-700" : "text-slate-900"
        }`}
      >
        {num}
      </div>
      <div
        className={`mt-1.5 text-[11px] font-medium ${
          emphasize ? "text-green-700 font-semibold" : "text-gray-500"
        }`}
      >
        {label}
      </div>
    </div>
  );
}

function ConvArrow() {
  return (
    <span className="flex-shrink-0 text-slate-300 px-1">
      <ChevronRight size={14} />
    </span>
  );
}

function ConvRate({ pct, label }) {
  return (
    <div className="text-center">
      <div className="text-base font-bold text-slate-900 -tracking-[0.01em]">
        {pct}
      </div>
      <div className="text-[10px] text-gray-400 mt-1 tracking-wider uppercase font-semibold">
        {label}
      </div>
    </div>
  );
}

// Monthly chart with current-year bars + prior-year YoY dashed trend line.
export function MonthlyChart({
  monthlyData,
  priorYearMonthly,
  currentMonth,
  selectedYear,
}) {
  // Compute scale.
  const maxCurrent = Math.max(...monthlyData.map((m) => m.netCommission), 0);
  const maxPrior = Math.max(...priorYearMonthly, 0);
  const maxValue = Math.max(maxCurrent, maxPrior, 1);
  const chartHeight = 160;
  const chartTop = 20;
  const chartBottom = 180;

  // Y-axis ticks (4 lines).
  const ticks = [maxValue, maxValue * 0.66, maxValue * 0.33, 0];

  // X positions: 12 bars between x=60 and x=676 (step ~55px).
  const xFor = (i) => 60 + i * 55;
  const yFor = (val) =>
    chartBottom - (val / maxValue) * (chartBottom - chartTop);

  return (
    <div className="pt-2 pb-1">
      <svg
        className="w-full h-[220px]"
        viewBox="0 0 720 220"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1E293B" />
            <stop offset="100%" stopColor="#475569" />
          </linearGradient>
          <linearGradient id="barGradLight" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#cbd5e1" />
            <stop offset="100%" stopColor="#e2e8f0" />
          </linearGradient>
        </defs>

        {/* Grid lines + Y-axis labels. */}
        {ticks.map((t, i) => {
          const y = chartTop + i * ((chartBottom - chartTop) / 3);
          return (
            <g key={i}>
              <line
                x1="40"
                y1={y}
                x2="710"
                y2={y}
                stroke={i === 3 ? "#cbd5e1" : "#f1f5f9"}
              />
              <text
                x="34"
                y={y + 4}
                fill="#9ca3af"
                fontSize="9.5"
                textAnchor="end"
              >
                ${formatNumber(t)}
              </text>
            </g>
          );
        })}

        {/* Prior-year trend line */}
        {maxPrior > 0 && (
          <polyline
            points={priorYearMonthly
              .map((v, i) => `${xFor(i) + 11},${yFor(v)}`)
              .join(" ")}
            fill="none"
            stroke="#9ca3af"
            strokeWidth="1.5"
            strokeDasharray="4 3"
            strokeLinecap="round"
          />
        )}

        {/* Current-year bars */}
        {monthlyData.map((m, i) => {
          const x = xFor(i);
          const isFuture = i > currentMonth;
          const isNow = i === currentMonth;
          const closedH = (m.closedCommission / maxValue) * chartHeight;
          const pendingH = (m.pendingCommission / maxValue) * chartHeight;
          const closedY = chartBottom - closedH;
          const pendingY = closedY - pendingH;
          return (
            <g key={i}>
              {isFuture && m.netCommission === 0 ? (
                <rect
                  x={x}
                  y={chartBottom - 6}
                  width="22"
                  height="6"
                  rx="3"
                  fill="#f1f5f9"
                />
              ) : (
                <>
                  {m.closedCommission > 0 && (
                    <rect
                      x={x}
                      y={closedY}
                      width="22"
                      height={Math.max(2, closedH)}
                      rx="3"
                      fill="url(#barGrad)"
                    >
                      <title>{`Closed: $${formatNumber(m.closedCommission)}`}</title>
                    </rect>
                  )}
                  {m.pendingCommission > 0 && (
                    <rect
                      x={x}
                      y={pendingY}
                      width="22"
                      height={Math.max(2, pendingH)}
                      rx={m.closedCommission > 0 ? 0 : 3}
                      fill="#f59e0b"
                      opacity="0.85"
                    >
                      <title>{`Pending: $${formatNumber(m.pendingCommission)}`}</title>
                    </rect>
                  )}
                </>
              )}
              {isNow && m.netCommission > 0 && (
                <text
                  x={x + 11}
                  y={pendingY - 6}
                  fill="#1E293B"
                  fontSize="10.5"
                  fontWeight="700"
                  textAnchor="middle"
                >
                  ${formatNumber(m.netCommission)}
                </text>
              )}
            </g>
          );
        })}

        {/* Month labels */}
        <g fill="#6b7280" fontSize="10.5" textAnchor="middle" fontWeight="500">
          {MONTH_NAMES_SHORT.map((mn, i) => {
            const isNow = i === currentMonth;
            const isEmptyFuture =
              i > currentMonth && monthlyData[i]?.netCommission === 0;
            return (
              <text
                key={i}
                x={xFor(i) + 11}
                y={isNow ? 208 : 200}
                fill={isNow ? "#1E293B" : isEmptyFuture ? "#cbd5e1" : "#6b7280"}
                fontWeight={isNow ? "700" : "500"}
              >
                {mn}
              </text>
            );
          })}
        </g>
      </svg>

      <div className="flex gap-4 items-center mt-3.5 pt-3 border-t border-black/[0.04]">
        <LegendItem label={`Closed ${selectedYear}`} colorClass="bg-[#1E293B]" />
        <LegendItem label="Pending" colorStyle="bg-amber-500 opacity-85" />
        <LegendItem
          label="Future"
          colorStyle="border border-dashed border-slate-300 bg-slate-200"
        />
        <LegendItem
          label={`${selectedYear - 1} YoY`}
          colorClass="!w-4 !h-[1.5px] bg-gray-400"
        />
      </div>
    </div>
  );
}

function LegendItem({ label, colorClass = "", colorStyle = "" }) {
  return (
    <div className="flex items-center gap-1.5 text-[11.5px] text-gray-500 font-medium">
      <span
        className={`w-2.5 h-2.5 rounded-sm ${colorClass} ${colorStyle}`}
      ></span>
      {label}
    </div>
  );
}

export function UpcomingClosingsPanel({
  title,
  deals,
  totalCommission,
  today,
  commissionForDeal,
  tight,
}) {
  const { getClientName } = useDealClientNames(deals);
  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-black/[0.04] mb-5 overflow-hidden flex flex-col ${
        tight ? "" : ""
      }`}
    >
      <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
        <div className="flex items-center gap-3">
          <span className="w-7 h-7 bg-[#1E293B] rounded-md inline-flex items-center justify-center flex-shrink-0">
            <Calendar size={14} strokeWidth={2} className="text-white" />
          </span>
          <div>
            <div className="text-[15px] font-semibold text-slate-900">
              {title}
            </div>
            {/* Header and rows are the same list: the dollar figure is the sum
               of the slices printed down the panel, nothing wider. It used to
               say "N deals · $X expected", the same words the hero's Pending
               Commission tile uses for a figure built a different way (that one
               spans the whole selected year and every deal that pays him, this
               one is only the deals below). Same words, different sums, one
               screen — so this one now says what it is instead. */}
            <div
              className="text-[11px] text-gray-500 mt-0.5"
              title="The commission on the deals listed below, added up."
            >
              {deals.length} {deals.length === 1 ? "deal" : "deals"} · $
              {Math.round(totalCommission || 0).toLocaleString("en-US")}{" "}
              in commission
            </div>
          </div>
        </div>
      </div>
      <div
        className="flex-1 overflow-y-auto"
        style={{ maxHeight: "480px" }}
      >
        {deals.length > 0 ? (
          deals.map((deal) => {
            const daysLeft = Math.ceil(
              (new Date(deal.closingdate) - today) / 86400000,
            );
            const isUrgent = daysLeft <= 7;
            const isSoon = daysLeft <= 14;
            const clientName = getClientName(deal);
            return (
              <Link
                key={deal.id || deal.createdat}
                to={`/dashboard/deals-detail/${deal.createdat}`}
                className="no-underline"
              >
                <div className="px-5 py-3 flex items-center gap-3.5 border-b border-slate-100 last:border-b-0 hover:bg-gray-50/50 transition cursor-pointer">
                  <div
                    className={`w-11 h-11 rounded-lg flex flex-col items-center justify-center flex-shrink-0 border ${
                      isUrgent
                        ? "bg-red-50 border-red-200"
                        : isSoon
                          ? "bg-amber-50 border-amber-200"
                          : "bg-slate-100 border-slate-200"
                    }`}
                  >
                    <span
                      className={`text-base font-bold leading-none ${
                        isUrgent
                          ? "text-red-700"
                          : isSoon
                            ? "text-amber-700"
                            : "text-slate-900"
                      }`}
                    >
                      {daysLeft}
                    </span>
                    <span
                      className={`text-[9px] mt-0.5 font-semibold tracking-wider ${
                        isUrgent
                          ? "text-red-500"
                          : isSoon
                            ? "text-amber-600"
                            : "text-gray-400"
                      }`}
                    >
                      DAYS
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[13.5px] text-slate-900 truncate">
                        {deal.propertyaddress}
                      </span>
                      <span
                        className={`flex-shrink-0 text-[9.5px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded ${
                          deal.dealStage === "Under Contract"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {deal.dealStage}
                      </span>
                    </div>
                    <div className="text-[11.5px] text-gray-500 mt-0.5 truncate">
                      {clientName} · {formatDate(deal.closingdate)}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {/* Rounded to whole dollars, these rows could not be added
                       up to the header above them: each row rounded on its own
                       and the header rounded the sum, so two rows of $7,666.50
                       printed as $7,667 + $7,667 under a header saying $15,333.
                       Full cents on both ends, and the column adds up. */}
                    <div className="font-semibold text-[13px] text-slate-900">
                      ${formatNumber(deal.offerPrice)}
                    </div>
                    <div className="text-[11.5px] text-green-700 font-semibold mt-0.5">
                      +${formatNumber(commissionForDeal(deal))}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })
        ) : (
          <div className="py-9 text-center text-gray-400 text-[13px]">
            <Calendar size={28} className="mx-auto text-gray-300 mb-2" />
            No upcoming closings
          </div>
        )}
      </div>
    </div>
  );
}

function MonthlyTablePanel({
  monthlyData,
  currentMonth,
  selectedUser,
  gciGoal,
  PendingDealsCount,
  closedDealsCount,
  ytdPendingVolume,
  ytdVolume,
  closedCommissionTotal,
  pendingCommissionTotal,
  closedPaidDealsYTD,
  pendingPaidDealsYTD,
  creditedTo,
  TotalPct,
  goalBasisNote,
  filterYear,
  yearDropdownVisible,
  setYearDropdownVisible,
  setFilterYear,
  setSelectedYear,
  setstatspopup,
  setselectedstat,
  setstatsdata,
  dashboardview,
}) {
  // Two axes on one table, and every cell that opens a list has to open the
  // list it is actually built from.
  //
  //   UNITS + VOLUME -> the deals CREDITED to this person. One deal, one owner.
  //   GCI            -> the deals that PAID this person, teammates' deals
  //                     included. The cell is the sum of that list, so that is
  //                     the list the cell opens.
  //
  // Before this, both counts and both GCI figures opened the same credited
  // list, so Henry's $11,210.20 GCI opened a drawer holding one $7,666 deal
  // and the $3,544.20 he earned on Joe's sale had no deal behind it anywhere.
  const UNITS_NOTE = `Deals credited to ${creditedTo} — one deal, one owner. Click to see them. The GCI column is a different list: every deal that PAID ${creditedTo}, teammates' deals included, so the two counts are not the same set.`;
  const GCI_NOTE = `Every dollar ${creditedTo} was paid, including slices earned on deals credited to a teammate. Click to see those deals; the rows add up to this figure. The unit columns count only the deals credited to ${creditedTo}.`;
  const openDeals = (title, list) => {
    setstatspopup(true);
    setselectedstat(title);
    setstatsdata(list);
  };

  const headers = [
    "Month",
    "Pending Units",
    "Closed Units",
    "Pending Volume",
    "Closed Volume",
    "Avg Price",
    "GCI",
    "Pending GCI",
    selectedUser?.role === "Employee" ? "GCI % of Goal" : null,
  ].filter(Boolean);

  // The unit columns and the GCI columns count different sets, so the header
  // row says which is which rather than leaving two numbers side by side to be
  // read as one.
  const headerNote = (h) => {
    if (h === "GCI % of Goal") return goalBasisNote;
    if (h === "Closed Units" || h === "Pending Units") return UNITS_NOTE;
    if (h === "GCI" || h === "Pending GCI") return GCI_NOTE;
    return undefined;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-black/[0.04] mb-5 overflow-hidden">
      <div className="px-6 py-5 border-b border-black/[0.08] flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="w-7 h-7 bg-[#1E293B] rounded-md inline-flex items-center justify-center flex-shrink-0 text-white">
            <BarChart3 size={14} strokeWidth={2} />
          </span>
          <div>
            <div className="text-[15px] font-semibold text-slate-900">
              Monthly Breakdown
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              Aggregated by closing date · {filterYear}
            </div>
          </div>
        </div>

        {dashboardview && (
          <div className="relative">
            <button
              onClick={() => setYearDropdownVisible(!yearDropdownVisible)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm bg-white"
            >
              {filterYear}
            </button>
            <YearFilterDropdown
              visible={yearDropdownVisible}
              onSelect={(year) => {
                setFilterYear(year);
                setSelectedYear(Number(year));
                setYearDropdownVisible(false);
              }}
              activateOutsideClick={() => setYearDropdownVisible(false)}
              style={{ top: "40px", right: "0px" }}
            />
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              {headers.map((h) => (
                <th
                  key={h}
                  title={headerNote(h)}
                  className={`px-3 py-2.5 text-[10.5px] font-semibold text-gray-500 uppercase tracking-wider ${
                    h !== "Month" ? "text-center" : "text-left pl-1"
                  }`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {monthlyData.map((m, i) => {
              const curr = i === currentMonth;
              // Gross commission on the deals credited to this person, over a
              // goal that was set in gross. The GCI column to the left is his
              // personal slice — a different, narrower number, and dividing it
              // by a gross-sized goal is what made this column read low.
              const pct =
                gciGoal > 0
                  ? ((m.closedCreditedGross / gciGoal) * 100).toFixed(1)
                  : 0;
              const dim = i > currentMonth + 2;
              return (
                <tr
                  key={m.month}
                  className={`border-b border-slate-100 ${
                    curr ? "bg-slate-100" : "hover:bg-gray-50"
                  } ${dim ? "opacity-55" : ""}`}
                >
                  <td className="px-3 py-3 pl-1 text-left font-semibold text-slate-900">
                    {MONTH_NAMES[m.month - 1]}
                    {curr && (
                      <span className="inline-block bg-[#1E293B] text-white text-[9px] font-bold px-1.5 py-0.5 rounded ml-2 tracking-wider">
                        NOW
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-center font-medium" title={UNITS_NOTE}>
                    {m.PendingDealsCount > 0 ? (
                      <span
                        className="cursor-pointer text-amber-700 font-semibold hover:underline"
                        onClick={() =>
                          openDeals(
                            `${MONTH_NAMES[m.month - 1]} · pending deals credited to ${creditedTo}`,
                            m.PendingDeals,
                          )
                        }
                      >
                        {m.PendingDealsCount}
                      </span>
                    ) : (
                      <span className="text-slate-300">0</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-center font-medium" title={UNITS_NOTE}>
                    {m.closedDealsCount > 0 ? (
                      <span
                        className="cursor-pointer text-[#1E293B] font-semibold hover:underline"
                        onClick={() =>
                          openDeals(
                            `${MONTH_NAMES[m.month - 1]} · closed deals credited to ${creditedTo}`,
                            m.closedDeals,
                          )
                        }
                      >
                        {m.closedDealsCount}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-center text-amber-700">
                    {m.monthlypendingvolume > 0
                      ? `$${formatNumber(m.monthlypendingvolume)}`
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-3 text-center">
                    {m.monthlyvolume > 0
                      ? `$${formatNumber(m.monthlyvolume)}`
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-3 text-center">
                    {m.monthlyvolume > 0 && m.closedDealsCount > 0
                      ? `$${formatNumber(m.monthlyvolume / m.closedDealsCount)}`
                      : <span className="text-slate-300">—</span>}
                  </td>
                  {/* GCI opens its OWN list — the deals that paid him this
                     month — instead of borrowing the credited list from the
                     Closed Units cell two columns over. The rows in that
                     drawer add up to this cell exactly. */}
                  <td className="px-3 py-3 text-center font-medium" title={GCI_NOTE}>
                    {m.closedPaidDeals.length > 0 ? (
                      <span
                        className="cursor-pointer text-[#1E293B] font-semibold hover:underline"
                        onClick={() =>
                          openDeals(
                            `${MONTH_NAMES[m.month - 1]} · closed deals that paid ${creditedTo}`,
                            m.closedPaidDeals,
                          )
                        }
                      >
                        ${formatNumber(m.closedCommission)}
                      </span>
                    ) : (
                      <span className="text-slate-300">$0.00</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-center text-amber-700" title={GCI_NOTE}>
                    {m.pendingPaidDeals.length > 0 ? (
                      <span
                        className="cursor-pointer font-semibold hover:underline"
                        onClick={() =>
                          openDeals(
                            `${MONTH_NAMES[m.month - 1]} · pending deals that pay ${creditedTo}`,
                            m.pendingPaidDeals,
                          )
                        }
                      >
                        ${formatNumber(m.pendingCommission)}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  {selectedUser?.role === "Employee" && (
                    <td className="px-3 py-3 text-center">
                      {gciGoal > 0 ? (
                        <span className="inline-flex items-center gap-1.5 justify-center">
                          {pct}%
                          <span className="inline-block w-11 h-1 rounded-sm bg-slate-100 overflow-hidden align-middle">
                            <span
                              className="block h-full rounded-sm bg-[#1E293B]"
                              style={{
                                width: `${Math.min(Number(pct), 100)}%`,
                              }}
                            />
                          </span>
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 border-t border-gray-200">
              <td className="px-3 py-3 pl-1 text-left text-[11px] uppercase tracking-wider font-bold text-[#1E293B]">
                YTD Total
              </td>
              <td className="px-3 py-3 text-center font-bold" title={UNITS_NOTE}>
                <span
                  className="cursor-pointer text-amber-700 hover:underline"
                  onClick={() =>
                    openDeals(
                      `${filterYear} · pending deals credited to ${creditedTo}`,
                      PendingDealsCount,
                    )
                  }
                >
                  {PendingDealsCount.length}
                </span>
              </td>
              <td className="px-3 py-3 text-center font-bold" title={UNITS_NOTE}>
                <span
                  className="cursor-pointer text-[#1E293B] hover:underline"
                  onClick={() =>
                    openDeals(
                      `${filterYear} · closed deals credited to ${creditedTo}`,
                      closedDealsCount,
                    )
                  }
                >
                  {closedDealsCount.length}
                </span>
              </td>
              <td className="px-3 py-3 text-center font-bold text-amber-700">
                ${formatNumber(ytdPendingVolume)}
              </td>
              <td className="px-3 py-3 text-center font-bold">
                ${formatNumber(ytdVolume)}
              </td>
              <td className="px-3 py-3 text-center font-bold">
                {closedDealsCount.length > 0
                  ? `$${formatNumber(ytdVolume / closedDealsCount.length)}`
                  : "—"}
              </td>
              {/* The year's GCI, and the deals behind it. This is the cell
                 Henry clicked: $11,210.20 with a drawer holding one $7,666
                 deal, because the drawer was the credited list. It now opens
                 every deal that paid him, each row his own slice, and the
                 rows add back up to the figure. */}
              <td className="px-3 py-3 text-center font-bold" title={GCI_NOTE}>
                {closedPaidDealsYTD.length > 0 ? (
                  <span
                    className="cursor-pointer text-[#1E293B] hover:underline"
                    onClick={() =>
                      openDeals(
                        `${filterYear} · closed deals that paid ${creditedTo}`,
                        closedPaidDealsYTD,
                      )
                    }
                  >
                    ${formatNumber(closedCommissionTotal)}
                  </span>
                ) : (
                  `$${formatNumber(closedCommissionTotal)}`
                )}
              </td>
              <td
                className="px-3 py-3 text-center font-bold text-amber-700"
                title={GCI_NOTE}
              >
                {pendingPaidDealsYTD.length > 0 ? (
                  <span
                    className="cursor-pointer hover:underline"
                    onClick={() =>
                      openDeals(
                        `${filterYear} · pending deals that pay ${creditedTo}`,
                        pendingPaidDealsYTD,
                      )
                    }
                  >
                    ${formatNumber(pendingCommissionTotal)}
                  </span>
                ) : (
                  `$${formatNumber(pendingCommissionTotal)}`
                )}
              </td>
              {selectedUser?.role === "Employee" && (
                <td className="px-3 py-3 text-center font-bold" title={goalBasisNote}>
                  {Number(TotalPct) > 0 ? `${TotalPct}%` : "—"}
                </td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default TeamPerformanceDetail;
